import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { TenantsAccessView } from "./TenantsAccessView";
import { IdentityRbacTab } from "./IdentityRbacTab";
import { IdentityModuleEntitiesTab } from "./IdentityModuleEntitiesTab";
import { IdentityComplianceTab } from "./IdentityComplianceTab";
import { EntityComplianceSection, EntityMfaSection } from "./EntitySecuritySections";
import { mcpCall } from "../lib/mcp";
import {
  AvatarUpload,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  EntityAvatar,
  Input,
  LastLoginSummary,
  Modal,
  PageHeader,
  RefreshButton,
  Select,
  StatusDot,
} from "../components/ui";
import type { LastLoginSummaryProps } from "../components/ui/LastLoginSummary";
import { Label } from "../components/ui/Label";
import {
  AlertTriangle,
  Bot,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  Cpu,
  Database,
  Edit,
  FolderGit2,
  Globe,
  History,
  Key,
  Layers,
  Loader2,
  Lock,
  LockOpen,
  LogOut,
  Plus,
  Search,
  Server,
  Settings,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  User,
  UserPlus,
  Users,
} from "lucide-react";
import {
  themedConfirm,
} from "../lib/themed-dialogs";

type EntityType =
  | "user"
  | "acm"
  | "fleet_client"
  | "fleet_server"
  | "service"
  | "agent"
  | "device"
  | "project";

interface Entity {
  id: string;
  type: EntityType;
  label: string;
  keycloak_sub: string | null;
  external_id: string | null;
  metadata: Record<string, unknown>;
  enabled: boolean;
  expires_at: string | null;
  protected: boolean;
  created_at: string;
  api_keys_count: number;
  ssh_keys_count: number;
  nodes_count: number;
}

interface EntityDetail {
  entity: Entity;
  /** Poslední úspěšné a neúspěšné přihlášení (identity.entity_get, záznam přihlášení). */
  last_login?: LastLoginSummaryProps;
  api_keys: Array<{
    id: string;
    label: string;
    scopes: string[];
    key_preview: string;
    enabled: boolean;
    created_at: string;
    last_used: string | null;
    use_count: number;
    protected: boolean;
  }>;
  ssh_keys: Array<
    { id: string; label: string; fingerprint: string; added_at: string }
  >;
  db_credentials?: Array<{
    id: string;
    username: string;
    db_name: string | null;
    privileges: string;
    instance_name: string;
    engine: string;
    created_at: string;
  }>;
  nodes: Array<{ acm_node_id: string; granted_at: string }>;
  timeline?: Array<{
    id: string;
    ts: string;
    category: string;
    event: string;
    identity: { entity_id?: string } | null;
    data: Record<string, unknown> | null;
  }>;
  groups?: Array<{
    id: string;
    name: string;
    description: string | null;
    metadata: Record<string, unknown>;
    added_at: string;
  }>;
  linked_projects?: Array<{
    kind: "orchestrator" | "workspace";
    project_id: string | null;
    slug: string;
    name: string;
    role: string;
    relationship: "membership" | "purpose_credential";
    linked_at: string | null;
  }>;
  linked_services?: Array<{
    id: string;
    type: string;
    label: string;
    external_id: string | null;
    enabled: boolean;
    identity_class: string | null;
    purpose: string | null;
    node_id: string | null;
    relationship: "created_by" | "member";
  }>;
  linked_users?: Array<{
    id: string;
    type: string;
    label: string;
    external_id: string | null;
    enabled: boolean;
    identity_class: string | null;
    purpose: string | null;
    node_id: string | null;
    relationship: "owner";
  }>;
}

interface EntityVaultItem {
  id: string;
  namespace: string;
  label: string;
  metadata: Record<string, unknown>;
  preview: string | null;
  fingerprint: string;
  access_scope: "user_only" | "user_and_node" | "user_and_node_and_escrow";
  updated_at: string;
}

const TYPE_META: Record<
  EntityType,
  { label: string; labelKey: string; icon: typeof User; color: string }
> = {
  user: {
    label: "Uživatelé",
    labelKey: "entities.typeUser",
    icon: User,
    color: "text-blue-400",
  },
  acm: {
    label: "ACM nody",
    labelKey: "entities.typeAcm",
    icon: Server,
    color: "text-emerald-400",
  },
  fleet_client: {
    label: "Fleet klienti",
    labelKey: "entities.typeFleetClient",
    icon: Cpu,
    color: "text-amber-400",
  },
  fleet_server: {
    label: "Fleet servery",
    labelKey: "entities.typeFleetServer",
    icon: Cpu,
    color: "text-purple-400",
  },
  service: {
    label: "Služby",
    labelKey: "entities.typeService",
    icon: Settings,
    color: "text-gray-400",
  },
  agent: {
    label: "Agenti",
    labelKey: "entities.typeAgent",
    icon: Bot,
    color: "text-cyan-400",
  },
  device: {
    label: "Zařízení",
    labelKey: "entities.typeDevice",
    icon: Cpu,
    color: "text-orange-400",
  },
  project: {
    label: "Projekty",
    labelKey: "entities.typeProject",
    icon: FolderGit2,
    color: "text-plex",
  },
};

const ALL_MODULES = [
  "acm",
  "mqtt",
  "supabase",
  "fleet",
  "keycloak",
  "npm",
  "catalog",
  "registry",
  "tasks",
  "toolbox",
  "wiki",
  "system-settings",
  "metrics",
  "loki",
];

export default function Entities() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  // Inline toggle pro enabled/disabled v list rowu
  const toggleEnabledMut = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) =>
      await mcpCall("identity.entity_update", { id, enabled }),
    onSuccess: (_, vars) => {
      toast.success(
        vars.enabled
          ? t("entities.entityActivated", "Entita aktivována")
          : t("entities.entityDeactivated", "Entita deaktivována"),
      );
      qc.invalidateQueries({ queryKey: ["entities"] });
      if (selectedId) {
        qc.invalidateQueries({ queryKey: ["entity-detail", selectedId] });
      }
    },
    onError: (e: Error) =>
      toast.error(
        t("entities.toggleFailed", "Toggle selhal: {{message}}", {
          message: e.message,
        }),
      ),
  });
  const [view, setView] = useState<
    "entities" | "groups" | "sessions" | "tenants" | "rbac" | "modules" | "compliance"
  >(
    "entities",
  );

  // Deep-link ?entity=<id> — z UserBadge/CollapsedFooter avataru v sidebaru.
  // Bez tohoto klik na vlastní účet nic nevybral (incident 2026-07-09).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const eid = searchParams.get("entity");
    const tab = searchParams.get("view");
    if (
      tab === "rbac" || tab === "groups" || tab === "sessions" ||
      tab === "tenants" ||
      tab === "modules" || tab === "compliance"
    ) {
      setView(tab);
    }
    if (eid) {
      setView("entities");
      setSelectedId(eid);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [filter, setFilter] = useState<EntityType | "all">("all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [grantOpen, setGrantOpen] = useState(false);
  const [keysExpanded, setKeysExpanded] = useState(false);
  const [sshOpen, setSshOpen] = useState(false);
  const [vaultOpen, setVaultOpen] = useState(false);
  const [vaultForm, setVaultForm] = useState({
    namespace: "custom",
    label: "",
    payload: '{\n  "secret": ""\n}',
    metadata: "{}",
    preview: "",
    access_scope: "user_and_node" as EntityVaultItem["access_scope"],
  });

  const vaultQ = useQuery({
    queryKey: ["entity-vault", selectedId],
    queryFn: () =>
      mcpCall<{ ok: boolean; items?: EntityVaultItem[] }>(
        "identity.vault_list",
        {
          entity_id: selectedId,
        },
      ),
    enabled: !!selectedId,
    staleTime: 30_000,
  });
  const vaultItems = vaultQ.data?.items ?? [];
  const vaultPutMut = useMutation({
    mutationFn: async () => {
      const payload = JSON.parse(vaultForm.payload) as Record<string, unknown>;
      const metadata = JSON.parse(vaultForm.metadata) as Record<
        string,
        unknown
      >;
      if (!payload || Array.isArray(payload) || typeof payload !== "object") {
        throw new Error(
          t(
            "entities.vaultPayloadMustBeObject",
            "Payload musí být JSON objekt",
          ),
        );
      }
      return await mcpCall<{ ok: boolean; error?: string }>(
        "identity.vault_put",
        {
          entity_id: selectedId,
          namespace: vaultForm.namespace.trim(),
          label: vaultForm.label.trim(),
          payload,
          metadata,
          preview: vaultForm.preview.trim() || undefined,
          access_scope: vaultForm.access_scope,
        },
      );
    },
    onSuccess: (result) => {
      if (!result.ok) {
        toast.error(
          result.error ?? t("entities.vaultWriteFailed", "Vault zápis selhal"),
        );
        return;
      }
      toast.success(t("entities.vaultStored", "Tajemství bylo zašifrováno"));
      setVaultOpen(false);
      setVaultForm({
        namespace: "custom",
        label: "",
        payload: '{\n  "secret": ""\n}',
        metadata: "{}",
        preview: "",
        access_scope: "user_and_node",
      });
      vaultQ.refetch();
    },
    onError: (error: Error) => toast.error(error.message),
  });
  const vaultDeleteMut = useMutation({
    mutationFn: (item: EntityVaultItem) =>
      mcpCall<{ ok: boolean }>("identity.vault_delete", {
        entity_id: selectedId,
        namespace: item.namespace,
        label: item.label,
      }),
    onSuccess: () => {
      toast.success(
        t("entities.vaultDeleted", "Vault položka byla zneplatněna"),
      );
      vaultQ.refetch();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  // „Přidat DB přístup" — založí v dbhost instanci usera reprezentujícího
  // entitu, spojeného od vzniku (entity-access-model). MCP-first.
  const [dbGrantOpen, setDbGrantOpen] = useState(false);
  const [dbGrantForm, setDbGrantForm] = useState({
    instance: "",
    username: "",
    db_name: "",
    privileges: "ALL",
  });
  const [dbGrantResult, setDbGrantResult] = useState<string | null>(null);
  const { data: dbInstancesData } = useQuery({
    queryKey: ["dbhost-instances-for-grant"],
    queryFn: () =>
      mcpCall<
        { ok: boolean; instances: Array<{ name: string; engine: string }> }
      >("dbhost.list"),
    enabled: dbGrantOpen,
    staleTime: 60_000,
  });
  const dbGrantMut = useMutation({
    mutationFn: () =>
      mcpCall<{ ok: boolean; error?: string; connection_string?: string }>(
        "dbhost.credentials",
        {
          instance: dbGrantForm.instance,
          action: "create",
          username: dbGrantForm.username,
          db_name: dbGrantForm.db_name || undefined,
          privileges: dbGrantForm.privileges,
          entity_id: selectedId,
        },
      ),
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.error ?? t("entities.createFailed", "Vytvoření selhalo"));
        return;
      }
      toast.success(t("entities.dbAccessCreated", "DB přístup vytvořen"));
      setDbGrantResult(r.connection_string ?? null);
      refetchDetail();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const [showKey, setShowKey] = useState<
    {
      key: string;
      label: string;
      mqtt?: { username: string; password: string; role: string } | null;
    } | null
  >(null);

  // Edit modal state
  const [editOpen, setEditOpen] = useState(false);
  const [editKcSearch, setEditKcSearch] = useState("");
  const [editKcSearchOpen, setEditKcSearchOpen] = useState(false);
  const [editForm, setEditForm] = useState<{
    label: string;
    type: EntityType;
    enabled: boolean;
    expires_at: string;
    keycloak_sub: string;
    external_id: string;
    acm_role: string;
    newPassword: string;
    removePassword: boolean;
    hasLocalPassword: boolean;
  }>({
    label: "",
    type: "user",
    enabled: true,
    expires_at: "",
    keycloak_sub: "",
    external_id: "",
    acm_role: "",
    newPassword: "",
    removePassword: false,
    hasLocalPassword: false,
  });

  // Password leak check (passwords.check_leak, volatelné z libovolného modulu)
  const [leakCheck, setLeakCheck] = useState<
    {
      leaked: boolean;
      occurrences: number;
      severity: "safe" | "low" | "medium" | "high" | "critical";
    } | null
  >(null);
  const leakCheckMut = useMutation({
    mutationFn: (password: string) =>
      mcpCall<{
        ok: boolean;
        leaked: boolean;
        occurrences: number;
        severity: "safe" | "low" | "medium" | "high" | "critical";
      }>("passwords.check_leak", { password }),
    onSuccess: (data) => setLeakCheck(data),
    onError: (e: Error) => {
      toast.error(e.message);
      setLeakCheck(null);
    },
  });

  // Create form
  const [createForm, setCreateForm] = useState({
    type: "user" as EntityType,
    label: "",
    keycloak_sub: "",
    external_id: "",
  });
  const [createMode, setCreateMode] = useState<"basic" | "kc_new" | "kc_link">(
    "basic",
  );
  const [kcUsername, setKcUsername] = useState("");
  const [kcEmail, setKcEmail] = useState("");
  const [kcFirstName, setKcFirstName] = useState("");
  const [kcLastName, setKcLastName] = useState("");
  const [kcPassword, setKcPassword] = useState("");
  const [kcRealmRoles, setKcRealmRoles] = useState<Record<string, boolean>>({});
  const [kcSearch, setKcSearch] = useState("");
  const [kcFirst, setKcFirst] = useState(0);
  const [kcFederation, setKcFederation] = useState("");
  const [kcSelected, setKcSelected] = useState<
    { id: string; username: string } | null
  >(null);
  const [acmRole, setAcmRole] = useState("");
  // Grant form
  const [grantModules, setGrantModules] = useState<
    Record<string, "admin" | "operator" | "viewer" | "none">
  >({});
  const [grantNodes, setGrantNodes] = useState<Record<string, boolean>>({});
  const [grantExpiryDays, setGrantExpiryDays] = useState<number>(0);

  // Load available nodes (self + peers)
  const { data: nodesData } = useQuery<
    {
      self: { id: string; label: string };
      peers: Array<{ id: string; label: string }>;
    }
  >({
    queryKey: ["entities-nodes"],
    queryFn: () => mcpCall("identity.entity_nodes_list"),
    staleTime: 60_000,
  });

  const { data: acmRolesData } = useQuery<{
    ok?: boolean;
    roles?: Array<{ id: string; label: string; description: string }>;
  }>({
    queryKey: ["identity-roles"],
    queryFn: () => mcpCall("identity.role_list"),
    staleTime: 5 * 60_000,
  });
  const acmRoleOptions = [
    { value: "", label: t("entities.optionNone", "— žádná") },
    ...(acmRolesData?.roles ?? []).map((r) => ({
      value: r.id,
      label: r.label,
    })),
  ];

  const { data: kcRolesData } = useQuery<
    {
      realm: string;
      roles: Array<{ id: string; name: string; description?: string }>;
    }
  >({
    queryKey: ["kc-realm-roles"],
    queryFn: () => mcpCall("sso.keycloak_realm_roles"),
    enabled: createOpen && createForm.type === "user" &&
      createMode === "kc_new",
    staleTime: 60_000,
  });
  const KC_PAGE = 30;
  const { data: kcSearchData, isFetching: kcSearching } = useQuery<
    {
      realm: string;
      users: Array<{
        id: string;
        username: string;
        email?: string;
        firstName?: string;
        lastName?: string;
        source?: string;
      }>;
      total?: number;
      first?: number;
      max?: number;
      federations?: Array<{ id: string; name: string }>;
    }
  >({
    queryKey: ["kc-user-search", kcSearch, kcFirst, kcFederation],
    queryFn: () =>
      mcpCall("sso.keycloak_list_users", {
        search: kcSearch.trim() || undefined,
        first: kcFirst,
        max: KC_PAGE,
        federation_id: kcFederation || undefined,
      }),
    enabled: createOpen && createForm.type === "user" &&
      createMode === "kc_link",
    staleTime: 10_000,
  });

  const { data: editKcSearchData } = useQuery<
    {
      realm: string;
      users: Array<
        {
          id: string;
          username: string;
          email?: string;
          firstName?: string;
          lastName?: string;
        }
      >;
    }
  >({
    queryKey: ["edit-kc-search", editKcSearch],
    queryFn: () =>
      mcpCall("sso.keycloak_list_users", { search: editKcSearch, max: 20 }),
    enabled: editOpen && editKcSearchOpen && editKcSearch.length >= 2,
    staleTime: 10_000,
  });
  // SSH form
  const [sshLabel, setSshLabel] = useState("");
  const [sshPubkey, setSshPubkey] = useState("");

  const { data: list, refetch: refetchList, isLoading } = useQuery<
    { entities: Entity[] }
  >({
    queryKey: ["entities", filter],
    queryFn: () =>
      mcpCall<{ entities: Entity[] }>(
        "identity.entity_list",
        filter === "all" ? {} : { type: filter },
      ),
  });
  const entities = list?.entities ?? [];
  const filteredEntities = entities.filter((e) => {
    if (filter !== "all" && e.type !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      const role =
        ((e.metadata as Record<string, unknown> | undefined)?.acm_role as
          | string
          | undefined) ?? "";
      return e.label.toLowerCase().includes(q) ||
        (e.keycloak_sub ?? "").toLowerCase().includes(q) ||
        (e.external_id ?? "").toLowerCase().includes(q) ||
        role.toLowerCase().includes(q) ||
        e.type.toLowerCase().includes(q);
    }
    return true;
  });

  const { data: detail, refetch: refetchDetail } = useQuery<EntityDetail>({
    queryKey: ["entity-detail", selectedId],
    queryFn: async () => {
      const r = await mcpCall<EntityDetail>("identity.entity_get", {
        id: selectedId!,
      });
      // Guard shape drift — UI čte .length/.map na polích.
      return {
        ...r,
        api_keys: Array.isArray(r?.api_keys) ? r.api_keys : [],
        ssh_keys: Array.isArray(r?.ssh_keys) ? r.ssh_keys : [],
        groups: Array.isArray((r as { groups?: unknown })?.groups)
          ? (r as { groups: EntityDetail["groups"] }).groups
          : [],
        timeline: Array.isArray((r as { timeline?: unknown })?.timeline)
          ? (r as { timeline: EntityDetail["timeline"] }).timeline
          : [],
      } as EntityDetail;
    },
    enabled: !!selectedId,
  });

  const resetCreateForm = () => {
    setCreateForm({
      type: "user",
      label: "",
      keycloak_sub: "",
      external_id: "",
    });
    setCreateMode("basic");
    setKcUsername("");
    setKcEmail("");
    setKcFirstName("");
    setKcLastName("");
    setKcPassword("");
    setKcRealmRoles({});
    setKcSearch("");
    setKcFirst(0);
    setKcFederation("");
    setKcSelected(null);
    setAcmRole("");
  };

  const createMut = useMutation({
    mutationFn: async () => {
      // One Identity-owned intent: the browser never coordinates Keycloak and
      // entity persistence itself, so the server can compensate safely.
      if (createForm.type === "user" && createMode !== "basic") {
        const payload: Record<string, unknown> = {
          label: createForm.label ||
            (createMode === "kc_link" ? kcSelected?.username : kcUsername),
          acm_role: acmRole || undefined,
        };
        if (createMode === "kc_link" && kcSelected) {
          payload.keycloak_sub = kcSelected.id;
          payload.keycloak_username = kcSelected.username;
        } else if (createMode === "kc_new") {
          payload.keycloak_create = {
            username: kcUsername,
            email: kcEmail || undefined,
            firstName: kcFirstName || undefined,
            lastName: kcLastName || undefined,
            password: kcPassword,
            realm_roles: Object.entries(kcRealmRoles).filter(([, v]) => v).map((
              [k],
            ) => k),
          };
        }
        return mcpCall<
          {
            ok: boolean;
            id: string;
            existed?: boolean;
            keycloak?: { id: string; username: string; realm?: string } | null;
          }
        >("identity.user_create", payload);
      }
      // Default: basic entity create
      return mcpCall<{ ok: boolean; id: string }>("identity.entity_create", {
        type: createForm.type,
        label: createForm.label,
        keycloak_sub: createForm.keycloak_sub || undefined,
        external_id: createForm.external_id || undefined,
        metadata: acmRole ? { acm_role: acmRole } : undefined,
      });
    },
    onSuccess: (
      r: {
        ok: boolean;
        error?: string;
        id: string;
        existed?: boolean;
        keycloak?: { id: string; username: string; realm: string } | null;
      },
    ) => {
      if (r.ok === false) {
        toast.error(
          String(r.error || t("entities.createFailed", "Vytvoření selhalo")),
        );
        return;
      }
      if (r.keycloak) {
        toast.success(
          r.existed
            ? t("entities.alreadyLinked", "Uživatel {{username}} už v ACM je", {
              username: r.keycloak.username,
            })
            : t(
              "entities.createdInKc",
              "Vytvořeno v KC ({{username}}) + ACM entity",
              {
                username: r.keycloak.username,
              },
            ),
        );
      } else {
        toast.success(t("entities.entityCreated", "Entity vytvořena"));
      }
      setCreateOpen(false);
      resetCreateForm();
      refetchList();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const editMut = useMutation({
    mutationFn: async (id: string) => {
      const cur =
        (detail?.entity?.metadata as Record<string, unknown> | undefined)
          ?.acm_role as
            | string
            | undefined;
      const meta = {
        ...(detail?.entity?.metadata as Record<string, unknown> ?? {}),
      };
      if (editForm.acm_role) meta.acm_role = editForm.acm_role;
      else if (cur) delete meta.acm_role;
      // Entity update
      await mcpCall("identity.entity_update", {
        id,
        label: editForm.label,
        type: editForm.type,
        enabled: editForm.enabled,
        expires_at: editForm.expires_at
          ? new Date(editForm.expires_at).toISOString()
          : null,
        keycloak_sub: editForm.keycloak_sub || null,
        external_id: editForm.external_id || null,
        metadata: meta,
      });
      // Password remains an Identity-owned use-case; the browser never selects a REST route.
      if (
        editForm.type === "user" &&
        (editForm.newPassword || editForm.removePassword)
      ) {
        await mcpCall("identity.entity_password_set", {
          id,
          password: editForm.removePassword
            ? null
            : editForm.newPassword || undefined,
        });
      }
    },
    onSuccess: () => {
      toast.success(t("entities.changesSaved", "Změny uloženy"));
      setEditOpen(false);
      refetchDetail();
      refetchList();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const [cloneResult, setCloneResult] = useState<
    {
      label: string;
      api_keys: { label: string; plaintext: string }[];
      llm_keys: { label: string; plaintext: string }[];
    } | null
  >(null);
  const [cloneKeysFlag, setCloneKeysFlag] = useState(true);
  const [confirmCloneFor, setConfirmCloneFor] = useState<
    {
      id: string;
      label: string;
      api_keys_count: number;
      ssh_keys_count: number;
    } | null
  >(null);

  const cloneMut = useMutation({
    mutationFn: ({ id, clone_keys }: { id: string; clone_keys: boolean }) =>
      mcpCall<{
        ok: boolean;
        new_entity_id?: string;
        label?: string;
        api_keys?: { label: string; plaintext: string }[];
        llm_keys?: { label: string; plaintext: string }[];
        error?: string;
      }>("identity.entity_clone", { source_entity_id: id, clone_keys }),
    onSuccess: (r) => {
      if (r.ok) {
        setCloneResult({
          label: r.label ?? t("entities.copyLabel", "kopie"),
          api_keys: r.api_keys ?? [],
          llm_keys: r.llm_keys ?? [],
        });
        qc.invalidateQueries({ queryKey: ["entities"] });
        toast.success(
          t("entities.entityCloned", "Entity naklonovaná: {{label}}", {
            label: r.label,
          }),
        );
      } else {
        toast.error(r.error ?? t("entities.cloneFailed", "Clone selhal"));
      }
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => mcpCall("identity.entity_delete", { id }),
    onSuccess: () => {
      toast.success(t("entities.entityDeleted", "Entity smazána (cascade)"));
      setSelectedId(null);
      refetchList();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const grantMut = useMutation({
    mutationFn: () => {
      const modules = Object.entries(grantModules)
        .filter(([, role]) => role !== "none")
        .map(([module, role]) => ({ module, role }));
      const node_ids = Object.entries(grantNodes)
        .filter(([, sel]) => sel)
        .map(([id]) => id);
      const payload: Record<string, unknown> = { modules, node_ids };
      if (grantExpiryDays > 0) payload.expires_days = grantExpiryDays;
      return mcpCall("identity.entity_access_grant", {
        id: selectedId,
        ...payload,
      });
    },
    onSuccess: (
      r: {
        key: string;
        label: string;
        scopes: string[];
        mqtt?: { username: string; password: string; role: string } | null;
      },
    ) => {
      toast.success(t("entities.keyCreated", "Klíč vytvořen"));
      setShowKey({ key: r.key, label: r.label, mqtt: r.mqtt ?? null });
      setGrantOpen(false);
      setGrantModules({});
      refetchDetail();
      refetchList();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const sshMut = useMutation({
    mutationFn: () =>
      mcpCall("identity.entity_ssh_add", {
        id: selectedId,
        label: sshLabel,
        public_key: sshPubkey,
      }),
    onSuccess: () => {
      toast.success(t("entities.sshKeyAdded", "SSH klíč přidán"));
      setSshOpen(false);
      setSshLabel("");
      setSshPubkey("");
      refetchDetail();
      refetchList();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const delKeyMut = useMutation({
    mutationFn: ({ ns, id }: { ns: string; id: string }) =>
      mcpCall("identity.api_key_revoke", { namespace: ns, id }),
    onSuccess: () => {
      toast.success(t("entities.keyDeleted", "Klíč smazán"));
      refetchDetail();
      refetchList();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const delSshMut = useMutation({
    mutationFn: (keyId: string) =>
      mcpCall("identity.entity_ssh_remove", { id: selectedId, key_id: keyId }),
    onSuccess: () => {
      toast.success(t("entities.sshKeyDeleted", "SSH klíč smazán"));
      refetchDetail();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("entities.pageTitle", "Entity (přístup)")}
        description={t(
          "entities.pageDescription",
          "Agregátor všech credentials — user, ACM, fleet, služby, agenti. Cascade delete.",
        )}
        icon={<Shield size={24} />}
        actions={
          <div className="flex gap-2">
            <RefreshButton
              onRefresh={() => refetchList()}
              loading={isLoading}
              size="md"
            />
            <Button
              variant="secondary"
              size="sm"
              icon={<UserPlus size={14} />}
              onClick={() => {
                setCreateForm((f) => ({ ...f, type: "user" }));
                setCreateMode("kc_link");
                setCreateOpen(true);
              }}
            >
              {t("entities.fromKeycloak", "Z Keycloaku")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={<Plus size={14} />}
              onClick={() => {
                setCreateForm((f) => ({ ...f, type: "user" }));
                setCreateMode("kc_new");
                setCreateOpen(true);
              }}
            >
              {t("entities.newEntity", "Nová entity")}
            </Button>
          </div>
        }
      />

      {/* View tabs (Entity / Skupiny) */}
      <div className="flex gap-1 border-b border-gray-800">
        <button
          onClick={() => setView("entities")}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            view === "entities"
              ? "border-blue-500 text-blue-400"
              : "border-transparent text-gray-500 hover:text-white"
          }`}
        >
          <Shield size={13} /> {t("entities.tabEntities", "Entity")}
        </button>
        <button
          onClick={() => setView("sessions")}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            view === "sessions"
              ? "border-blue-500 text-blue-400"
              : "border-transparent text-gray-500 hover:text-white"
          }`}
        >
          <Globe size={13} />{" "}
          {t("entities.tabActiveSessions", "Aktivní sessions")}
        </button>
        <button
          onClick={() => setView("tenants")}
          className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
            view === "tenants"
              ? "border-blue-500 text-blue-400"
              : "border-transparent text-gray-500 hover:text-white"
          }`}
        >
          <Users size={13} className="inline mr-1" />{" "}
          {t("entities.tabTenants", "Tenanti")}
        </button>
        <button
          onClick={() => setView("groups")}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            view === "groups"
              ? "border-blue-500 text-blue-400"
              : "border-transparent text-gray-500 hover:text-white"
          }`}
        >
          <Layers size={13} /> {t("entities.tabGroups", "Skupiny")}
        </button>
        <button
          onClick={() => setView("rbac")}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            view === "rbac"
              ? "border-blue-500 text-blue-400"
              : "border-transparent text-gray-500 hover:text-white"
          }`}
        >
          <Key size={13} /> {t("entities.tabRbac", "RBAC")}
        </button>
        <button
          onClick={() => setView("modules")}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            view === "modules"
              ? "border-blue-500 text-blue-400"
              : "border-transparent text-gray-500 hover:text-white"
          }`}
        >
          <Layers size={13} /> {t("entities.tabModules", "Z modulů")}
        </button>
        <button
          onClick={() => setView("compliance")}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            view === "compliance"
              ? "border-blue-500 text-blue-400"
              : "border-transparent text-gray-500 hover:text-white"
          }`}
        >
          <ShieldCheck size={13} /> {t("entities.tabCompliance", "Compliance")}
        </button>
      </div>

      {view === "sessions"
        ? <SessionsView />
        : view === "tenants"
        ? <TenantsAccessView />
        : view === "groups"
        ? <GroupsView />
        : view === "rbac"
        ? <IdentityRbacTab />
        : view === "modules"
        ? <IdentityModuleEntitiesTab />
        : view === "compliance"
        ? <IdentityComplianceTab />
        : (
          <>
            {/* Filter tabs */}
            <div className="flex gap-1 border-b border-gray-800 overflow-x-auto">
              <button
                onClick={() => setFilter("all")}
                className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                  filter === "all"
                    ? "border-blue-400 text-white"
                    : "border-transparent text-gray-500 hover:text-gray-300"
                }`}
              >
                {t("entities.filterAll", "Vše")} ({entities.length})
              </button>
              {(Object.entries(TYPE_META) as [
                EntityType,
                typeof TYPE_META[EntityType],
              ][]).map(
                ([type, meta]) => {
                  const count = entities.filter((e) => e.type === type).length;
                  const Icon = meta.icon;
                  return (
                    <button
                      key={type}
                      onClick={() => setFilter(type)}
                      className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                        filter === type
                          ? "border-blue-400 text-white"
                          : "border-transparent text-gray-500 hover:text-gray-300"
                      }`}
                    >
                      <Icon size={12} className={meta.color} />
                      {t(meta.labelKey, meta.label)}
                      {filter === "all" || filter === type ? ` (${count})` : ""}
                    </button>
                  );
                },
              )}
            </div>

            {/* Search */}
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t(
                  "entities.searchPlaceholder",
                  "Hledat label, keycloak_sub, external_id, role, typ…",
                )}
                className="w-full bg-gray-950 border border-gray-800 rounded pl-9 pr-3 py-2 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-blue-500"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white text-xs"
                >
                  ✕
                </button>
              )}
            </div>

            <div
              className={`grid gap-4 ${
                selectedId ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"
              }`}
            >
              {/* List */}
              <Card>
                <CardContent className="p-0">
                  {entities.length === 0
                    ? (
                      <EmptyState
                        icon={<Shield size={24} />}
                        title={t("entities.noEntitiesTitle", "Žádné entity")}
                        description={t(
                          "entities.noEntitiesDescription",
                          "Vytvoř první entity — user, service nebo ACM peer.",
                        )}
                      />
                    )
                    : (
                      <table className="w-full text-sm">
                        <thead className="border-b border-gray-800 text-left text-xs text-gray-500 uppercase">
                          <tr>
                            <th className="px-3 py-2">
                              {t("entities.colType", "Typ")}
                            </th>
                            <th className="px-3 py-2">Label</th>
                            <th className="px-3 py-2">Auth</th>
                            <th className="px-3 py-2">Role</th>
                            <th className="px-3 py-2 text-center">
                              {t("entities.colKeys", "Klíče")}
                            </th>
                            <th className="px-3 py-2 text-center">SSH</th>
                            <th className="px-3 py-2 text-center">
                              {t("entities.colStatus", "Stav")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredEntities.length === 0 && search && (
                            <tr>
                              <td
                                colSpan={7}
                                className="px-3 py-4 text-center text-xs text-gray-500"
                              >
                                {t(
                                  "entities.noResultsFor",
                                  'Žádný výsledek pro „{{search}}".',
                                  {
                                    search,
                                  },
                                )}
                              </td>
                            </tr>
                          )}
                          {filteredEntities.map((e) => {
                            const Icon = TYPE_META[e.type].icon;
                            const sel = selectedId === e.id;
                            return (
                              <tr
                                key={e.id}
                                onClick={() => setSelectedId(e.id)}
                                className={`border-b border-gray-800/50 last:border-0 cursor-pointer transition-colors ${
                                  sel
                                    ? "bg-blue-500/10"
                                    : "hover:bg-gray-800/30"
                                }`}
                              >
                                <td className="px-3 py-2.5">
                                  {e.type === "user"
                                    ? (
                                      <EntityAvatar
                                        label={e.label}
                                        avatar={(e.metadata as
                                          | Record<string, unknown>
                                          | null)
                                          ?.avatar as string ?? null}
                                        size={24}
                                      />
                                    )
                                    : (
                                      <Icon
                                        size={14}
                                        className={TYPE_META[e.type].color}
                                      />
                                    )}
                                </td>
                                <td className="px-3 py-2.5">
                                  <div className="flex items-center gap-2">
                                    <span className="text-white text-sm">
                                      {e.label}
                                    </span>
                                    {e.protected && (
                                      <Badge variant="warning">🔒</Badge>
                                    )}
                                    {e.keycloak_sub && (
                                      <Badge variant="secondary">SSO</Badge>
                                    )}
                                    {e.type === "user" && e.keycloak_sub && (
                                      ((e.metadata.mfa as { configured?: string[]; setup_pending?: boolean } | undefined)?.configured?.length ?? 0) > 0
                                        ? <Badge variant="success">2FA</Badge>
                                        : (e.metadata.mfa as { setup_pending?: boolean } | undefined)?.setup_pending
                                        ? <Badge variant="warning">2FA</Badge>
                                        : null
                                    )}
                                  </div>
                                  {e.external_id && (
                                    <code className="font-mono text-[10px] text-gray-500">
                                      {e.external_id}
                                    </code>
                                  )}
                                </td>
                                <td className="px-3 py-2.5 text-center text-xs text-gray-300">
                                  {e.api_keys_count}
                                </td>
                                <td className="px-3 py-2.5 text-center text-xs text-gray-300">
                                  {e.ssh_keys_count}
                                </td>
                                <td className="px-3 py-2.5 text-center">
                                  <div className="flex items-center justify-center gap-1.5">
                                    <StatusDot
                                      status={e.enabled ? "running" : "stopped"}
                                    />
                                    {!e.protected && !e.id.startsWith("self") &&
                                      !e.id.startsWith("mn_") &&
                                      !e.id.startsWith("fa_") && (
                                      <button
                                        type="button"
                                        title={e.enabled
                                          ? t(
                                            "entities.deactivate",
                                            "Deaktivovat",
                                          )
                                          : t("entities.activate", "Aktivovat")}
                                        disabled={toggleEnabledMut.isPending}
                                        onClick={(ev) => {
                                          ev.stopPropagation();
                                          toggleEnabledMut.mutate({
                                            id: e.id,
                                            enabled: !e.enabled,
                                          });
                                        }}
                                        className="p-1 rounded hover:bg-gray-700 disabled:opacity-50"
                                      >
                                        {e.enabled
                                          ? (
                                            <Lock
                                              size={12}
                                              className="text-gray-400 hover:text-amber-400"
                                            />
                                          )
                                          : (
                                            <LockOpen
                                              size={12}
                                              className="text-gray-400 hover:text-emerald-400"
                                            />
                                          )}
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                </CardContent>
              </Card>

              {/* Detail */}
              {selectedId && detail && (
                <Card>
                  <CardContent className="space-y-4 py-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-4">
                        {/* Avatar mění jen user — roboti/služby/nody mají statickou ikonu typu */}
                        {detail.entity.type === "user"
                          ? (
                            <AvatarUpload
                              label={detail.entity.label}
                              avatar={(detail.entity.metadata as
                                | Record<string, unknown>
                                | undefined)
                                ?.avatar as string ?? null}
                              size={64}
                              onChange={async (dataUrl) => {
                                const meta = {
                                  ...(detail.entity.metadata as Record<
                                    string,
                                    unknown
                                  > ?? {}),
                                  avatar: dataUrl,
                                };
                                await mcpCall("identity.entity_update", {
                                  id: detail.entity.id,
                                  metadata: meta,
                                });
                                toast.success(
                                  t("entities.avatarSaved", "Avatar uložen"),
                                );
                                refetchDetail();
                                refetchList();
                              }}
                            />
                          )
                          : (() => {
                            const TypeIcon = TYPE_META[detail.entity.type].icon;
                            return (
                              <div className="w-16 h-16 rounded-2xl bg-gray-900 border border-gray-800 flex items-center justify-center shrink-0">
                                <TypeIcon
                                  size={28}
                                  className={TYPE_META[detail.entity.type]
                                    .color}
                                />
                              </div>
                            );
                          })()}
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-lg font-bold text-white leading-none">
                              {detail.entity.label}
                            </h3>
                            <StatusDot
                              status={detail.entity.enabled
                                ? "running"
                                : "stopped"}
                            />
                          </div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Badge variant="blue">
                              {t(
                                TYPE_META[detail.entity.type].labelKey,
                                TYPE_META[detail.entity.type].label,
                              )}
                            </Badge>
                            {(() => {
                              const role = (detail.entity.metadata as
                                | Record<string, unknown>
                                | undefined)?.acm_role as string | undefined;
                              return role
                                ? <Badge variant="yellow">{role}</Badge>
                                : null;
                            })()}
                            {detail.entity.keycloak_sub && (
                              <Badge variant="purple">SSO</Badge>
                            )}
                            {detail.entity.protected && (
                              <Badge variant="yellow">🔒</Badge>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(detail.entity.id);
                              toast.success(
                                t("entities.idCopied", "ID zkopírováno"),
                              );
                            }}
                            className="flex items-center gap-1 text-[10px] text-gray-600 hover:text-gray-300 font-mono transition-colors"
                            title={t("entities.copyId", "Kopírovat ID")}
                          >
                            {detail.entity.id} <Copy size={9} />
                          </button>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setSelectedId(null)}
                        >
                          {t("entities.close", "Zavřít")}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          icon={<Edit size={12} />}
                          onClick={() => {
                            const meta = (detail.entity.metadata as
                              | Record<string, unknown>
                              | undefined) ?? {};
                            setEditForm({
                              label: detail.entity.label,
                              type: detail.entity.type,
                              enabled: detail.entity.enabled,
                              expires_at: detail.entity.expires_at ?? "",
                              keycloak_sub: detail.entity.keycloak_sub ?? "",
                              external_id: (detail.entity as {
                                external_id?: string | null;
                              }).external_id ??
                                "",
                              acm_role: typeof meta.acm_role === "string"
                                ? meta.acm_role
                                : "",
                              newPassword: "",
                              removePassword: false,
                              hasLocalPassword:
                                !!(detail.entity as Record<string, unknown>)
                                  .has_local_password,
                            });
                            setLeakCheck(null);
                            setEditOpen(true);
                          }}
                        >
                          {t("entities.edit", "Upravit")}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<Copy size={12} />}
                          loading={cloneMut.isPending}
                          onClick={() =>
                            setConfirmCloneFor({
                              id: detail.entity.id,
                              label: detail.entity.label,
                              api_keys_count: detail.api_keys.length,
                              ssh_keys_count: detail.ssh_keys.length,
                            })}
                        >
                          {t("entities.clone", "Klonovat")}
                        </Button>
                        {!detail.entity.protected && (
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<Trash2 size={12} />}
                            onClick={async () => {
                              if (
                                await themedConfirm(
                                  t(
                                    "entities.confirmDeleteEntity",
                                    'Smazat entity "{{label}}"? Cascade smaže {{keys}} klíčů + {{ssh}} SSH.',
                                    {
                                      label: detail.entity.label,
                                      keys: detail.api_keys.length,
                                      ssh: detail.ssh_keys.length,
                                    },
                                  ),
                                )
                              ) {
                                deleteMut.mutate(detail.entity.id);
                              }
                            }}
                          >
                            {t("entities.delete", "Smazat")}
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Identity card */}
                    <div className="p-3 bg-gray-900/40 rounded-xl space-y-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Keycloak sub</span>
                        <code className="font-mono text-gray-300">
                          {detail.entity.keycloak_sub
                            ? `${detail.entity.keycloak_sub.slice(0, 12)}…`
                            : "—"}
                        </code>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">External ID</span>
                        <code className="font-mono text-gray-300">
                          {detail.entity.external_id ?? "—"}
                        </code>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">
                          {t("entities.createdAt", "Vytvořeno")}
                        </span>
                        <span className="text-gray-300">
                          {new Date(detail.entity.created_at).toLocaleString(
                            "cs-CZ",
                          )}
                        </span>
                      </div>
                    </div>

                    {/* Keycloak user card */}
                    {detail.entity.keycloak_sub && (
                      <KcUserCard
                        sub={detail.entity.keycloak_sub}
                        entityId={detail.entity.id}
                        onChanged={() => {
                          refetchDetail();
                          refetchList();
                        }}
                      />
                    )}

                    {/* Durable module/project relationships projected by Identity. */}
                    {(detail.linked_projects?.length ?? 0) > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Layers size={14} className="text-blue-400" />
                          <span className="text-sm font-medium text-white">
                            {t("entities.linkedProjects", "Propojené projekty")}
                            {" "}
                            ({detail.linked_projects!.length})
                          </span>
                        </div>
                        <div className="space-y-1">
                          {detail.linked_projects!.map((project, index) => (
                            <div
                              key={`${project.kind}:${
                                project.project_id ?? project.slug
                              }:${project.role}:${index}`}
                              className="p-2 bg-gray-900/40 rounded text-xs"
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-gray-200 font-medium truncate">
                                  {project.name || project.slug}
                                </span>
                                <Badge
                                  variant={project.kind === "orchestrator"
                                    ? "info"
                                    : "purple"}
                                >
                                  {project.kind}
                                </Badge>
                                <Badge variant="gray">{project.role}</Badge>
                                {project.relationship ===
                                    "purpose_credential" && (
                                  <Badge variant="warning">credential</Badge>
                                )}
                              </div>
                              <code className="block mt-1 text-[10px] text-gray-600 break-all">
                                {project.project_id ?? project.slug}
                              </code>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {((detail.linked_services?.length ?? 0) > 0 ||
                      (detail.linked_users?.length ?? 0) > 0) && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Bot size={14} className="text-violet-400" />
                          <span className="text-sm font-medium text-white">
                            {t(
                              "entities.linkedIdentities",
                              "Propojené služby a identity",
                            )} ({(detail.linked_services?.length ?? 0) +
                              (detail.linked_users?.length ?? 0)})
                          </span>
                        </div>
                        <div className="space-y-1">
                          {[
                            ...(detail.linked_users ?? []),
                            ...(detail.linked_services ?? []),
                          ].map(
                            (linked) => (
                              <div
                                key={`${linked.relationship}:${linked.id}`}
                                className="p-2 bg-gray-900/40 rounded text-xs"
                              >
                                <div className="flex items-center gap-2">
                                  <StatusDot
                                    status={linked.enabled
                                      ? "online"
                                      : "offline"}
                                  />
                                  <span className="text-gray-200 font-medium truncate">
                                    {linked.label}
                                  </span>
                                  <Badge variant="gray">{linked.type}</Badge>
                                  {linked.identity_class && (
                                    <Badge variant="purple">
                                      {linked.identity_class}
                                    </Badge>
                                  )}
                                  <Badge variant="info">
                                    {linked.relationship}
                                  </Badge>
                                </div>
                                <div className="mt-1 text-[10px] text-gray-600 font-mono break-all">
                                  {[
                                    linked.purpose,
                                    linked.external_id,
                                    linked.node_id,
                                  ]
                                    .filter(Boolean).join(" · ") || linked.id}
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    )}

                    {/* API keys — collapsed by default */}
                    <div>
                      <div
                        className="flex items-center justify-between mb-2 cursor-pointer"
                        onClick={() => setKeysExpanded(!keysExpanded)}
                      >
                        <div className="flex items-center gap-2">
                          <Key size={14} className="text-gray-400" />
                          <span className="text-sm font-medium text-white">
                            {t("entities.apiKeysCount", "API klíče")}{" "}
                            ({detail.api_keys.length})
                          </span>
                          <span className="text-gray-600 text-xs">
                            {keysExpanded ? "▼" : "▶"}
                          </span>
                        </div>
                        <Button
                          variant="primary"
                          size="sm"
                          icon={<Plus size={12} />}
                          onClick={async (e) => {
                            e.stopPropagation();
                            setGrantOpen(true);
                          }}
                        >
                          Grant
                        </Button>
                      </div>
                      {!keysExpanded
                        ? (
                          <p className="text-xs text-gray-600 py-1">
                            {t(
                              "entities.clickToExpand",
                              "Klikni pro rozbalení",
                            )}
                          </p>
                        )
                        : detail.api_keys.length === 0
                        ? (
                          <p className="text-xs text-gray-500 py-2">
                            {t(
                              "entities.noApiKeys",
                              "Žádné klíče. Použij Grant pro vytvoření.",
                            )}
                          </p>
                        )
                        : (
                          <div className="space-y-1">
                            {detail.api_keys.map((k) => (
                              <div
                                key={k.id}
                                className="flex items-center justify-between p-2 bg-gray-900/40 rounded text-xs"
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="text-gray-300 truncate">
                                    {k.label}
                                  </div>
                                  <code className="font-mono text-[10px] text-gray-500">
                                    {k.key_preview} • {k.scopes.length} scopes
                                  </code>
                                </div>
                                {k.protected
                                  ? <Badge variant="warning">🔒</Badge>
                                  : (
                                    <button
                                      onClick={async () => {
                                        if (
                                          await themedConfirm(
                                            t(
                                              "entities.confirmDeleteKey",
                                              "Smazat klíč {{label}}?",
                                              {
                                                label: k.label,
                                              },
                                            ),
                                          )
                                        ) {
                                          delKeyMut.mutate({
                                            ns: detail.entity.type,
                                            id: k.id,
                                          });
                                        }
                                      }}
                                      className="text-gray-600 hover:text-red-400 p-1"
                                    >
                                      <Trash2 size={11} />
                                    </button>
                                  )}
                              </div>
                            ))}
                          </div>
                        )}
                    </div>

                    {/* SSH keys */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Shield size={14} className="text-gray-400" />
                          <span className="text-sm font-medium text-white">
                            {t("entities.sshKeysCount", "SSH klíče")}{" "}
                            ({detail.ssh_keys.length})
                          </span>
                        </div>
                        <Button
                          variant="primary"
                          size="sm"
                          icon={<UserPlus size={12} />}
                          onClick={() => setSshOpen(true)}
                        >
                          {t("entities.add", "Přidat")}
                        </Button>
                      </div>
                      {detail.ssh_keys.length === 0
                        ? (
                          <p className="text-xs text-gray-500 py-2">
                            {t("entities.noSshKeys", "Žádné SSH klíče.")}
                          </p>
                        )
                        : (
                          <div className="space-y-1">
                            {detail.ssh_keys.map((s) => (
                              <div
                                key={s.id}
                                className="flex items-center justify-between p-2 bg-gray-900/40 rounded text-xs"
                              >
                                <div>
                                  <div className="text-gray-300">{s.label}</div>
                                  <code className="font-mono text-[10px] text-gray-500 break-all">
                                    {s.fingerprint}
                                  </code>
                                </div>
                                <button
                                  onClick={async () => {
                                    if (
                                      await themedConfirm(
                                        t(
                                          "entities.confirmDeleteSshKey",
                                          "Smazat SSH klíč {{label}}?",
                                          { label: s.label },
                                        ),
                                      )
                                    ) {
                                      delSshMut.mutate(s.id);
                                    }
                                  }}
                                  className="text-gray-600 hover:text-red-400 p-1"
                                >
                                  <Trash2 size={11} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>

                    {/* Identity vault — encrypted arbitrary secrets/OAuth data. */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Lock size={14} className="text-emerald-400" />
                          <span className="text-sm font-medium text-white">
                            {t("entities.vaultItems", "Bezpečné úložiště")}{" "}
                            ({vaultItems.length})
                          </span>
                        </div>
                        <Button
                          variant="primary"
                          size="sm"
                          icon={<Plus size={12} />}
                          onClick={() => setVaultOpen(true)}
                        >
                          {t("entities.add", "Přidat")}
                        </Button>
                      </div>
                      <p className="text-[11px] text-gray-600 mb-2">
                        {t(
                          "entities.vaultHint",
                          "API klíče, OAuth tokeny a libovolná strukturovaná tajemství. Seznam nikdy nevrací plaintext.",
                        )}
                      </p>
                      {vaultItems.length === 0
                        ? (
                          <p className="text-xs text-gray-500 py-1">
                            {t(
                              "entities.noVaultItems",
                              "Žádné uložené tajemství.",
                            )}
                          </p>
                        )
                        : (
                          <div className="space-y-1">
                            {vaultItems.map((item) => (
                              <div
                                key={item.id}
                                className="flex items-center justify-between p-2 bg-gray-900/40 rounded text-xs"
                              >
                                <div className="min-w-0">
                                  <div className="text-gray-300 truncate">
                                    <span className="text-gray-600">
                                      {item.namespace}/
                                    </span>
                                    {item.label}
                                  </div>
                                  <div className="text-[10px] text-gray-600 truncate">
                                    {item.preview ||
                                      item.fingerprint.slice(0, 16)} ·{" "}
                                    {item.access_scope}
                                  </div>
                                </div>
                                <button
                                  onClick={async () => {
                                    if (
                                      await themedConfirm(
                                        t(
                                          "entities.confirmDeleteVault",
                                          "Zneplatnit vault položku {{label}}?",
                                          { label: item.label },
                                        ),
                                      )
                                    ) {
                                      vaultDeleteMut.mutate(item);
                                    }
                                  }}
                                  className="text-gray-600 hover:text-red-400 p-1"
                                >
                                  <Trash2 size={11} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>

                    {
                      /* DB přístupy — linky entity na DB usery (entity-access-model).
                        „Přidat" založí v instanci usera reprezentujícího entitu,
                        spojený od vzniku. */
                    }
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Database size={14} className="text-gray-400" />
                          <span className="text-sm font-medium text-white">
                            {t("entities.dbAccessCount", "Databázové přístupy")}
                            {" "}
                            ({detail.db_credentials?.length ?? 0})
                          </span>
                        </div>
                        <Button
                          variant="primary"
                          size="sm"
                          icon={<UserPlus size={12} />}
                          onClick={() => {
                            setDbGrantForm({
                              instance: "",
                              username: detail.entity.label.toLowerCase()
                                .replace(/[^a-z0-9_]/g, "_").slice(0, 32),
                              db_name: "",
                              privileges: "ALL",
                            });
                            setDbGrantResult(null);
                            setDbGrantOpen(true);
                          }}
                        >
                          {t("entities.add", "Přidat")}
                        </Button>
                      </div>
                      {(detail.db_credentials?.length ?? 0) === 0
                        ? (
                          <p className="text-xs text-gray-500 py-1">
                            {t("entities.noDbAccess", "Žádné DB přístupy.")}
                          </p>
                        )
                        : (
                          <div className="space-y-1">
                            {detail.db_credentials!.map((c) => (
                              <div
                                key={c.id}
                                className="flex items-center gap-2 p-2 bg-gray-900/40 rounded text-xs"
                              >
                                <code className="font-mono text-white">
                                  {c.username}
                                </code>
                                <span className="text-gray-600">@</span>
                                <span className="text-gray-300">
                                  {c.instance_name}
                                </span>
                                <Badge variant="gray">{c.engine}</Badge>
                                {c.privileges === "adopted" && (
                                  <Badge variant="purple">
                                    {t("entities.adopted", "adoptovaný")}
                                  </Badge>
                                )}
                                <span className="flex-1" />
                                <span className="text-[10px] text-gray-600 font-mono">
                                  {c.db_name ??
                                    t("entities.allDatabases", "všechny DB")} ·
                                  {" "}
                                  {c.privileges}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>

                    {/* Poslední přihlášení a odkud (záznam přihlášení) */}
                    <LastLoginSummary {...(detail.last_login ?? {})} />

                    {/* Sessions */}
                    {(detail as Record<string, unknown>).sessions &&
                      ((detail as Record<string, unknown>).sessions as Array<
                          Record<string, unknown>
                        >).length > 0 &&
                      (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Globe size={14} className="text-emerald-400" />
                            <span className="text-sm font-medium text-white">
                              {t("entities.activeLogins", "Aktivní přihlášení")}
                              ({((detail as Record<string, unknown>)
                                .sessions as unknown[]).length})
                            </span>
                          </div>
                          <div className="space-y-1">
                            {((detail as Record<string, unknown>)
                              .sessions as Array<
                                Record<string, unknown>
                              >).map((s) => (
                                <div
                                  key={s.id as string}
                                  className="flex items-center justify-between p-2 bg-gray-900/40 rounded text-xs"
                                >
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span
                                        className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${
                                          s.auth_type === "keycloak"
                                            ? "bg-blue-500/15 text-blue-400"
                                            : "bg-gray-700 text-gray-400"
                                        }`}
                                      >
                                        {s.auth_type === "keycloak"
                                          ? "SSO"
                                          : t("entities.password", "heslo")}
                                      </span>
                                      <span className="text-gray-300">
                                        {(s.domain as string) ||
                                          (s.node_id as string)}
                                      </span>
                                    </div>
                                    <div className="text-[10px] text-gray-500 mt-0.5">
                                      {s.ip_address && (
                                        <span>
                                          IP: {s.ip_address as string} ·
                                        </span>
                                      )}
                                      <span>
                                        {new Date(s.created_at as string)
                                          .toLocaleString("cs")}
                                      </span>
                                    </div>
                                  </div>
                                  <button
                                    onClick={async () => {
                                      if (
                                        !(await themedConfirm(
                                          t(
                                            "entities.confirmLogout",
                                            "Odhlásit tuto session?",
                                          ),
                                        ))
                                      ) return;
                                      await mcpCall(
                                        "identity.entity_session_revoke",
                                        {
                                          id: detail.entity.id,
                                          session_id: s.id,
                                        },
                                      );
                                      toast.success(
                                        t(
                                          "entities.sessionLoggedOut",
                                          "Session odhlášena",
                                        ),
                                      );
                                      refetchDetail();
                                    }}
                                    className="text-gray-600 hover:text-red-400 p-1 shrink-0"
                                    title={t("entities.logout", "Odhlásit")}
                                  >
                                    <LogOut size={11} />
                                  </button>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                    {/* Zabezpečení přihlášení + Compliance (jen uživatelé) */}
                    {detail.entity.type === "user" && (
                      <>
                        {detail.entity.keycloak_sub && (
                          <EntityMfaSection
                            entityId={detail.entity.id}
                            metadata={detail.entity.metadata}
                            onChanged={() => refetchDetail()}
                          />
                        )}
                        <EntityComplianceSection
                          entityId={detail.entity.id}
                          tenantId={(detail.entity as { tenant_id?: string | null }).tenant_id ?? null}
                        />
                      </>
                    )}

                    {/* Groups */}
                    {detail.groups && detail.groups.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Layers size={14} className="text-gray-400" />
                          <span className="text-sm font-medium text-white">
                            {t("entities.groupsCount", "Skupiny")}{" "}
                            ({detail.groups.length})
                          </span>
                        </div>
                        <div className="space-y-1">
                          {detail.groups.map((g) => (
                            <div
                              key={g.id}
                              className="flex items-center justify-between p-2 bg-gray-900/40 rounded text-xs"
                            >
                              <div>
                                <span className="text-gray-300 font-medium">
                                  {g.name}
                                </span>
                                {(g.metadata?.acm_role as string | undefined) &&
                                  (
                                    <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 text-[10px] font-mono">
                                      {g.metadata.acm_role as string}
                                    </span>
                                  )}
                                {g.description && (
                                  <p className="text-gray-500">
                                    {g.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Timeline */}
                    {detail.timeline && detail.timeline.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <History size={14} className="text-gray-400" />
                          <span className="text-sm font-medium text-white">
                            {t("entities.activityCount", "Aktivita")}{" "}
                            ({detail.timeline.length})
                          </span>
                        </div>
                        <div className="space-y-1 max-h-64 overflow-y-auto">
                          {detail.timeline.map((entry) => (
                            <div
                              key={entry.id}
                              className="flex items-start gap-2 p-2 bg-gray-900/40 rounded text-xs"
                            >
                              <span className="text-gray-500 font-mono whitespace-nowrap">
                                {new Date(entry.ts).toLocaleString("cs-CZ", {
                                  month: "2-digit",
                                  day: "2-digit",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                              <span className="inline-block px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 text-[10px]">
                                {entry.category}
                              </span>
                              <span className="text-gray-300 font-medium">
                                {entry.event}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </>
        )}

      {/* Edit Entity Modal */}
      {editOpen && detail && (
        <Modal
          open
          onClose={() => setEditOpen(false)}
          title={t("entities.editEntityTitle", "Upravit: {{label}}", {
            label: detail.entity.label,
          })}
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setEditOpen(false);
                  setEditKcSearchOpen(false);
                  setEditKcSearch("");
                }}
              >
                {t("entities.cancel", "Zrušit")}
              </Button>
              <Button
                variant="primary"
                loading={editMut.isPending}
                onClick={() => editMut.mutate(detail.entity.id)}
              >
                {t("entities.save", "Uložit")}
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            {/* ── Základní ── */}
            <div className="space-y-3">
              <div className="text-[10px] font-semibold text-gray-600 uppercase tracking-widest">
                {t("entities.sectionBasic", "Základní")}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Label</Label>
                  <Input
                    size="sm"
                    className="mt-1"
                    value={editForm.label}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, label: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>
                    {t("entities.fieldType", "Typ")}{" "}
                    <span
                      className="text-amber-400"
                      title={t(
                        "entities.typeChangeWarning",
                        "Pozor — mění klasifikaci entity",
                      )}
                    >
                      *
                    </span>
                  </Label>
                  <Select
                    size="sm"
                    className="mt-1"
                    value={editForm.type}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        type: e.target.value as EntityType,
                      }))}
                    options={(Object.entries(TYPE_META) as [
                      EntityType,
                      typeof TYPE_META[EntityType],
                    ][]).map(([k, m]) => ({
                      value: k,
                      label: `${m.label} (${k})`,
                    }))}
                  />
                </div>
              </div>
              <div>
                <Label>External ID</Label>
                <Input
                  size="sm"
                  className="mt-1"
                  value={editForm.external_id}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, external_id: e.target.value }))}
                  placeholder={t(
                    "entities.externalIdPlaceholder",
                    "např. github-ci, hostname, ...",
                  )}
                />
              </div>
            </div>

            {/* ── Přihlášení ── */}
            <div className="pt-3 border-t border-gray-800 space-y-3">
              <div className="text-[10px] font-semibold text-gray-600 uppercase tracking-widest">
                {t("entities.sectionLogin", "Přihlášení")}
              </div>
              <div>
                <Label>{t("entities.keycloakLink", "Keycloak napojení")}</Label>
                {editForm.keycloak_sub
                  ? (
                    <div className="mt-1 flex items-center gap-2 p-2 bg-blue-950/20 border border-blue-500/20 rounded">
                      <Badge variant="info" className="text-[10px]">SSO</Badge>
                      <code className="flex-1 font-mono text-xs text-blue-300 break-all">
                        {editForm.keycloak_sub}
                      </code>
                      <button
                        type="button"
                        onClick={() =>
                          setEditForm((f) => ({ ...f, keycloak_sub: "" }))}
                        className="text-gray-500 hover:text-red-400 p-1"
                        title={t("entities.disconnect", "Odpojit")}
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  )
                  : (
                    <div className="mt-1 space-y-2">
                      {!editKcSearchOpen
                        ? (
                          <Button
                            variant="secondary"
                            size="sm"
                            icon={<Search size={12} />}
                            onClick={() => setEditKcSearchOpen(true)}
                          >
                            {t(
                              "entities.findLinkKcUser",
                              "Najít a napojit Keycloak usera",
                            )}
                          </Button>
                        )
                        : (
                          <>
                            <input
                              autoFocus
                              type="text"
                              className="w-full bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-white"
                              value={editKcSearch}
                              onChange={(e) => setEditKcSearch(e.target.value)}
                              placeholder={t(
                                "entities.kcSearchPlaceholder",
                                "Hledat (min 2 znaky): username, email, jméno…",
                              )}
                            />
                            {editKcSearchData && (
                              <div className="space-y-1 max-h-40 overflow-y-auto">
                                {editKcSearchData.users.length === 0
                                  ? (
                                    <p className="text-xs text-gray-500 p-2">
                                      {t(
                                        "entities.noResults",
                                        "Žádný výsledek.",
                                      )}
                                    </p>
                                  )
                                  : editKcSearchData.users.map((u) => (
                                    <button
                                      key={u.id}
                                      type="button"
                                      onClick={() => {
                                        setEditForm((f) => ({
                                          ...f,
                                          keycloak_sub: u.id,
                                        }));
                                        setEditKcSearchOpen(false);
                                        setEditKcSearch("");
                                      }}
                                      className="w-full text-left p-2 rounded border bg-gray-900/40 border-gray-800 hover:border-blue-500/40 text-xs"
                                    >
                                      <div className="text-white font-medium">
                                        {u.username}
                                      </div>
                                      <div className="text-[10px] text-gray-500">
                                        {u.email ?? "—"} · {u.firstName ?? ""}
                                        {" "}
                                        {u.lastName ?? ""} ·{" "}
                                        <code>{u.id.slice(0, 8)}…</code>
                                      </div>
                                    </button>
                                  ))}
                              </div>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditKcSearchOpen(false);
                                setEditKcSearch("");
                              }}
                            >
                              {t("entities.cancel", "Zrušit")}
                            </Button>
                          </>
                        )}
                      <details className="text-xs">
                        <summary className="cursor-pointer text-gray-500 hover:text-gray-300">
                          {t(
                            "entities.orEnterSubManually",
                            "nebo zadat sub UUID ručně",
                          )}
                        </summary>
                        <input
                          className="mt-2 w-full bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-white font-mono"
                          value={editForm.keycloak_sub}
                          onChange={(e) =>
                            setEditForm((f) => ({
                              ...f,
                              keycloak_sub: e.target.value,
                            }))}
                          placeholder="cd518daf-295c-..."
                        />
                      </details>
                    </div>
                  )}
              </div>
            </div>

            {/* ── Přístup ── */}
            <div className="pt-3 border-t border-gray-800 space-y-3">
              <div className="text-[10px] font-semibold text-gray-600 uppercase tracking-widest">
                {t("entities.sectionAccess", "Přístup")}
              </div>
              <div className="grid grid-cols-2 gap-3 items-end">
                <div>
                  <Label>ACM role</Label>
                  <Select
                    size="sm"
                    className="mt-1"
                    value={editForm.acm_role}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        acm_role: e.target.value,
                      }))}
                    options={acmRoleOptions}
                  />
                </div>
                <label className="flex items-center gap-2 h-8 px-3 rounded-xl border border-gray-800 bg-gray-900/40 text-sm text-gray-300 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="accent-blue-500"
                    checked={editForm.enabled}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, enabled: e.target.checked }))}
                  />
                  Enabled
                </label>
              </div>

              <div>
                <Label>
                  {t("entities.fieldExpiry", "Expirace přístupu")}{" "}
                  <span className="text-xs text-gray-500">
                    {t(
                      "entities.expiryHint",
                      "(nepovinné — entita po datu přestane validovat auth)",
                    )}
                  </span>
                </Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    size="sm"
                    type="datetime-local"
                    className="flex-1 font-mono"
                    value={editForm.expires_at
                      ? editForm.expires_at.slice(0, 16)
                      : ""}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        expires_at: e.target.value,
                      }))}
                  />
                  {editForm.expires_at && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        setEditForm((f) => ({ ...f, expires_at: "" }))}
                    >
                      {t("entities.noExpiryButton", "Bez expirace")}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {editForm.type === "user" && (
              <div className="border-t border-gray-800 pt-3 space-y-2">
                <div className="text-[10px] font-semibold text-gray-600 uppercase tracking-widest">
                  {t("entities.sectionLocalPassword", "Lokální heslo")}
                </div>
                <Label>
                  <span className="text-xs text-gray-500">
                    {t(
                      "entities.localPasswordHint",
                      "Per-instance emergency login (NE replikováno)",
                    )}
                  </span>
                </Label>
                {editForm.hasLocalPassword && (
                  <p className="text-xs text-emerald-400">
                    {t("entities.passwordIsSet", "Heslo je nastavené.")}
                  </p>
                )}
                <input
                  type="password"
                  className="w-full bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-white"
                  value={editForm.newPassword}
                  onChange={(e) => {
                    setLeakCheck(null);
                    setEditForm((f) => ({
                      ...f,
                      newPassword: e.target.value,
                      removePassword: false,
                    }));
                  }}
                  placeholder={editForm.hasLocalPassword
                    ? t(
                      "entities.newPasswordPlaceholder",
                      "nové heslo (prázdné = beze změny)",
                    )
                    : t(
                      "entities.setPasswordPlaceholder",
                      "nastavit heslo (volitelné)",
                    )}
                  disabled={editForm.removePassword}
                />
                {editForm.newPassword && !editForm.removePassword && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="text-xs text-gray-400 hover:text-white underline flex items-center gap-1"
                      disabled={leakCheckMut.isPending}
                      onClick={() => leakCheckMut.mutate(editForm.newPassword)}
                    >
                      {leakCheckMut.isPending
                        ? <Loader2 size={12} className="animate-spin" />
                        : <Shield size={12} />}
                      {t("entities.checkLeak", "Zkontrolovat únik hesla")}
                    </button>
                    {leakCheck && (
                      leakCheck.leaked
                        ? (
                          <span
                            className={`text-xs flex items-center gap-1 ${
                              leakCheck.severity === "critical" ||
                                leakCheck.severity === "high"
                                ? "text-red-400"
                                : "text-orange-400"
                            }`}
                          >
                            {leakCheck.severity === "critical"
                              ? <AlertTriangle size={12} />
                              : <ShieldAlert size={12} />}
                            {t(
                              "entities.leakFound",
                              "Uniklo {{count}}× ({{severity}})",
                              {
                                count: leakCheck.occurrences.toLocaleString(
                                  "cs-CZ",
                                ),
                                severity: leakCheck.severity,
                              },
                            )}
                          </span>
                        )
                        : (
                          <span className="text-xs text-emerald-400 flex items-center gap-1">
                            <ShieldCheck size={12} />
                            {t("entities.leakNotFound", "Bez úniku")}
                          </span>
                        )
                    )}
                  </div>
                )}
                {editForm.hasLocalPassword && (
                  <label className="flex items-center gap-2 text-xs text-red-400">
                    <input
                      type="checkbox"
                      checked={editForm.removePassword}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          removePassword: e.target.checked,
                          newPassword: "",
                        }))}
                    />
                    {t(
                      "entities.removeLocalPassword",
                      "Odstranit lokální heslo (jen SSO)",
                    )}
                  </label>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Create Modal */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t("entities.newEntity", "Nová entity")}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              {t("entities.cancel", "Zrušit")}
            </Button>
            <Button
              variant="primary"
              onClick={() => createMut.mutate()}
              loading={createMut.isPending}
              disabled={createForm.type === "user" && createMode === "kc_new"
                ? (!kcUsername || !kcPassword)
                : createForm.type === "user" && createMode === "kc_link"
                ? !kcSelected
                : !createForm.label}
            >
              {createMode === "kc_link"
                ? t("entities.importUser", "Importovat")
                : t("entities.create", "Vytvořit")}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <Label>{t("entities.fieldType", "Typ")}</Label>
            <Select
              className="mt-1 w-full"
              value={createForm.type}
              onChange={(e) => {
                const type = e.target.value as EntityType;
                setCreateForm((f) => ({ ...f, type }));
                if (type === "user" && createMode === "basic") {setCreateMode(
                    "kc_new",
                  );}
              }}
            >
              {(Object.entries(TYPE_META) as [
                EntityType,
                typeof TYPE_META[EntityType],
              ][]).map((
                [typeKey, m],
              ) => (
                <option key={typeKey} value={typeKey}>
                  {t(m.labelKey, m.label)}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label>Label *</Label>
            <input
              className="mt-1 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white"
              value={createForm.label}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, label: e.target.value }))}
              placeholder={t(
                "entities.createLabelPlaceholder",
                "např. vector@mazec.cz",
              )}
            />
          </div>
          <div>
            <Label>
              {t("entities.externalIdOptional", "External ID (volitelné)")}
            </Label>
            <input
              className="mt-1 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white"
              value={createForm.external_id}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, external_id: e.target.value }))}
              placeholder={t(
                "entities.createExternalIdPlaceholder",
                "např. github-ci, acm-instance-uuid",
              )}
            />
          </div>
          {createForm.type === "user" && (
            <>
              <div className="flex gap-1 border-b border-gray-800">
                {([
                  {
                    id: "basic",
                    label: t("entities.tabBasic", "Basic (jen entity)"),
                  },
                  {
                    id: "kc_new",
                    label: t("entities.tabCreateInKc", "Nový uživatel"),
                  },
                  {
                    id: "kc_link",
                    label: t(
                      "entities.tabLinkExistingKc",
                      "Importovat (KC / AD / IdP)",
                    ),
                  },
                ] as Array<{ id: typeof createMode; label: string }>).map((
                  tab,
                ) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setCreateMode(tab.id)}
                    className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px ${
                      createMode === tab.id
                        ? "border-blue-500 text-blue-400"
                        : "border-transparent text-gray-500 hover:text-white"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div>
                <Label>ACM role</Label>
                <Select
                  size="sm"
                  value={acmRole}
                  onChange={(e) => setAcmRole(e.target.value)}
                  className="mt-1 w-full"
                  options={acmRoleOptions.map((o) =>
                    o.value === ""
                      ? {
                        value: "",
                        label: t(
                          "entities.noRoleGrantOption",
                          "— žádná (přidělí se přes grant nebo skupinu)",
                        ),
                      }
                      : o
                  )}
                />
              </div>

              {createMode === "basic" && (
                <div>
                  <Label>
                    {t(
                      "entities.keycloakSubOptional",
                      "Keycloak sub (volitelné — manuální napojení)",
                    )}
                  </Label>
                  <input
                    className="mt-1 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white font-mono"
                    value={createForm.keycloak_sub}
                    onChange={(e) =>
                      setCreateForm((f) => ({
                        ...f,
                        keycloak_sub: e.target.value,
                      }))}
                    placeholder="cd518daf-..."
                  />
                </div>
              )}

              {createMode === "kc_new" && (
                <>
                  <div className="p-2 bg-blue-950/20 border border-blue-500/20 rounded text-xs text-blue-300">
                    {t(
                      "entities.kcNewInfo",
                      "Vytvoří usera v Keycloaku ({{realm}}) + lokální entity.",
                      { realm: kcRolesData?.realm ?? "master" },
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Username *</Label>
                      <input
                        className="mt-1 w-full bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-white"
                        value={kcUsername}
                        onChange={(e) => setKcUsername(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <input
                        className="mt-1 w-full bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-white"
                        value={kcEmail}
                        onChange={(e) => setKcEmail(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>First name</Label>
                      <input
                        className="mt-1 w-full bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-white"
                        value={kcFirstName}
                        onChange={(e) => setKcFirstName(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Last name</Label>
                      <input
                        className="mt-1 w-full bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-white"
                        value={kcLastName}
                        onChange={(e) => setKcLastName(e.target.value)}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>
                      {t("entities.initialPasswordLabel", "Počáteční heslo")} *
                      {" "}
                      <span className="text-xs text-gray-500">
                        {t(
                          "entities.initialPasswordHint",
                          "(user musí změnit při prvním loginu)",
                        )}
                      </span>
                    </Label>
                    <input
                      type="password"
                      className="mt-1 w-full bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-white"
                      value={kcPassword}
                      onChange={(e) => setKcPassword(e.target.value)}
                    />
                  </div>
                  {kcRolesData && kcRolesData.roles.length > 0 && (
                    <div>
                      <Label>KC realm roles</Label>
                      <div className="mt-1 space-y-1 max-h-32 overflow-y-auto p-2 bg-gray-900/40 rounded">
                        {kcRolesData.roles.map((r) => (
                          <label
                            key={r.id}
                            className="flex items-center gap-2 text-xs text-gray-300"
                          >
                            <input
                              type="checkbox"
                              checked={kcRealmRoles[r.name] ?? false}
                              onChange={(e) =>
                                setKcRealmRoles((g) => ({
                                  ...g,
                                  [r.name]: e.target.checked,
                                }))}
                            />
                            <code>{r.name}</code>
                            {r.description && (
                              <span className="text-gray-500">
                                — {r.description}
                              </span>
                            )}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {createMode === "kc_link" && (
                <>
                  <p className="text-xs text-gray-500">
                    {t(
                      "entities.kcLinkHint",
                      "Procházej uživatele, na které Keycloak dosáhne (realm, AD/LDAP, IdP). Import založí lokální entitu — nemusí existovat předem.",
                    )}
                  </p>
                  <div className="flex gap-2">
                    <input
                      className="flex-1 bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-white"
                      value={kcSearch}
                      onChange={(e) => {
                        setKcSearch(e.target.value);
                        setKcFirst(0);
                      }}
                      placeholder={t(
                        "entities.searchKcPlaceholder",
                        "jméno, email, nebo prázdné = první strana",
                      )}
                    />
                    {(kcSearchData?.federations?.length ?? 0) > 0 && (
                      <select
                        className="bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-xs text-white"
                        value={kcFederation}
                        onChange={(e) => {
                          setKcFederation(e.target.value);
                          setKcFirst(0);
                        }}
                      >
                        <option value="">
                          {t("entities.allSources", "Všechny zdroje")}
                        </option>
                        {kcSearchData!.federations!.map((f) => (
                          <option key={f.id} value={f.id}>{f.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className="space-y-1 max-h-56 overflow-y-auto">
                    {kcSearching && (
                      <p className="text-xs text-gray-500 p-2">
                        {t("common.loading", "Načítám…")}
                      </p>
                    )}
                    {kcSearchData && kcSearchData.users.length === 0 &&
                        !kcSearching
                      ? (
                        <p className="text-xs text-gray-500 p-2">
                          {t("entities.noResults", "Žádný výsledek.")}
                        </p>
                      )
                      : (kcSearchData?.users ?? []).map((u) => {
                        const imported = entities.some((e) =>
                          e.keycloak_sub === u.id
                        );
                        return (
                          <button
                            key={u.id}
                            type="button"
                            onClick={() => {
                              setKcSelected({ id: u.id, username: u.username });
                              setCreateForm((f) => ({
                                ...f,
                                label: f.label || u.username,
                              }));
                            }}
                            className={`w-full text-left p-2 rounded border text-xs ${
                              kcSelected?.id === u.id
                                ? "bg-blue-950/30 border-blue-500/40 text-blue-300"
                                : "bg-gray-900/40 border-gray-800 text-gray-300 hover:border-gray-700"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium">{u.username}</span>
                              <span className="text-[10px] text-gray-500">
                                {u.source ?? "keycloak"}
                                {imported
                                  ? ` · ${t("entities.alreadyInAcm", "v ACM")}`
                                  : ""}
                              </span>
                            </div>
                            <div className="text-[10px] text-gray-500">
                              {[u.firstName, u.lastName].filter(Boolean).join(
                                " ",
                              ) || "—"}
                              {" · "}
                              {u.email ?? "—"}
                            </div>
                          </button>
                        );
                      })}
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-gray-500">
                    <span>
                      {t("entities.kcPage", "{{from}}–{{to}} z {{total}}", {
                        from: (kcSearchData?.total ?? 0) === 0
                          ? 0
                          : kcFirst + 1,
                        to: kcFirst + (kcSearchData?.users.length ?? 0),
                        total: kcSearchData?.total ?? 0,
                      })}
                    </span>
                    <span className="flex gap-2">
                      <button
                        type="button"
                        className="text-blue-400 disabled:text-gray-600"
                        disabled={kcFirst <= 0}
                        onClick={() =>
                          setKcFirst((n) => Math.max(0, n - KC_PAGE))}
                      >
                        {t("common.prev", "Předchozí")}
                      </button>
                      <button
                        type="button"
                        className="text-blue-400 disabled:text-gray-600"
                        disabled={(kcSearchData?.users.length ?? 0) < KC_PAGE}
                        onClick={() => setKcFirst((n) => n + KC_PAGE)}
                      >
                        {t("common.next", "Další")}
                      </button>
                    </span>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </Modal>

      {/* Grant Access Modal */}
      <Modal
        open={grantOpen}
        onClose={() => {
          setGrantOpen(false);
          setGrantModules({});
          setGrantNodes({});
          setGrantExpiryDays(0);
        }}
        title={t("entities.grantModalTitle", "Grant access — vygenerovat klíč")}
        footer={
          <>
            <Button variant="secondary" onClick={() => setGrantOpen(false)}>
              {t("entities.cancel", "Zrušit")}
            </Button>
            <Button
              variant="primary"
              onClick={() => grantMut.mutate()}
              loading={grantMut.isPending}
              disabled={Object.values(grantModules).every((r) =>
                r === "none" || !r
              )}
            >
              {t("entities.generateKey", "Vygenerovat klíč")}
            </Button>
          </>
        }
      >
        <div className="space-y-2 max-h-96 overflow-y-auto">
          <p className="text-xs text-gray-500 mb-2">
            {t(
              "entities.grantDescription",
              "Vyber modul a roli. Klíč bude obsahovat odpovídající scopes (admin = read+write+admin).",
            )}
          </p>
          {nodesData && (nodesData.peers.length > 0) && (
            <div className="mb-3 p-2 bg-gray-900/40 rounded space-y-1">
              <div className="text-xs font-medium text-gray-400 mb-1">
                {t(
                  "entities.nodesWithAccess",
                  "Nodes s přístupem (entity_node_access)",
                )}
              </div>
              {[
                { id: nodesData.self.id, label: nodesData.self.label },
                ...nodesData.peers,
              ].map((
                n,
              ) => (
                <label
                  key={n.id}
                  className="flex items-center gap-2 text-xs text-gray-300"
                >
                  <input
                    type="checkbox"
                    checked={grantNodes[n.id] ?? false}
                    onChange={(e) =>
                      setGrantNodes((g) => ({
                        ...g,
                        [n.id]: e.target.checked,
                      }))}
                  />
                  {n.label}
                </label>
              ))}
              <p className="text-[10px] text-gray-600 pt-1">
                {t(
                  "entities.nodesHint",
                  "Pokud nic nevybereš, entity dostane klíč ale entity_node_access zůstane prázdný (SSH distribuce to nevezme).",
                )}
              </p>
            </div>
          )}
          <div className="mb-3 p-2 bg-gray-900/40 rounded flex items-center justify-between gap-2">
            <div>
              <div className="text-xs font-medium text-gray-400">
                {t("entities.keyExpiryLabel", "Expirace klíče (dní)")}
              </div>
              <p className="text-[10px] text-gray-600">
                {t(
                  "entities.keyExpiryHint",
                  "0 = bez expirace. Task key-rotation-watchdog automaticky disabluje expired klíče.",
                )}
              </p>
            </div>
            <input
              type="number"
              min="0"
              max="3650"
              className="w-24 bg-gray-950 border border-gray-800 rounded px-2 py-1 text-xs text-white"
              value={grantExpiryDays}
              onChange={(e) =>
                setGrantExpiryDays(parseInt(e.target.value || "0", 10))}
            />
          </div>
          {ALL_MODULES.map((mod) => (
            <div
              key={mod}
              className="flex items-center justify-between p-2 bg-gray-900/40 rounded"
            >
              <span className="text-sm text-gray-300 font-mono">{mod}</span>
              <Select
                size="sm"
                value={grantModules[mod] ?? "none"}
                onChange={(e) =>
                  setGrantModules((g) => ({
                    ...g,
                    [mod]: e.target.value as
                      | "admin"
                      | "operator"
                      | "viewer"
                      | "none",
                  }))}
              >
                <option value="none">—</option>
                <option value="viewer">
                  {t("entities.optionViewerRead", "Viewer (read)")}
                </option>
                <option value="operator">
                  {t(
                    "entities.optionOperatorReadWrite",
                    "Operator (read+write)",
                  )}
                </option>
                <option value="admin">
                  {t("entities.optionAdminFull", "Admin (full)")}
                </option>
              </Select>
            </div>
          ))}
        </div>
      </Modal>

      {/* SSH Modal */}
      <Modal
        open={sshOpen}
        onClose={() => setSshOpen(false)}
        title={t("entities.addSshKeyTitle", "Přidat SSH klíč")}
        footer={
          <>
            <Button variant="secondary" onClick={() => setSshOpen(false)}>
              {t("entities.cancel", "Zrušit")}
            </Button>
            <Button
              variant="primary"
              onClick={() => sshMut.mutate()}
              loading={sshMut.isPending}
              disabled={!sshLabel || !sshPubkey}
            >
              {t("entities.add", "Přidat")}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <Label>Label *</Label>
            <input
              className="mt-1 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white"
              value={sshLabel}
              onChange={(e) => setSshLabel(e.target.value)}
              placeholder={t(
                "entities.sshLabelPlaceholder",
                "např. laptop-vector",
              )}
            />
          </div>
          <div>
            <Label>Public key *</Label>
            <textarea
              className="mt-1 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-xs text-white font-mono"
              rows={4}
              value={sshPubkey}
              onChange={(e) => setSshPubkey(e.target.value)}
              placeholder="ssh-ed25519 AAAA... user@host"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              {t(
                "entities.sshReconcileHint",
                "ssh_reconcile task (5min interval) zapíše tento klíč do authorized_keys na nodech kde má entity přístup.",
              )}
            </p>
          </div>
        </div>
      </Modal>

      {/* Encrypted Identity vault item. Plaintext exists only in this one POST. */}
      <Modal
        open={vaultOpen}
        onClose={() => setVaultOpen(false)}
        title={t("entities.addVaultItem", "Přidat do bezpečného úložiště")}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setVaultOpen(false)}>
              {t("entities.cancel", "Zrušit")}
            </Button>
            <Button
              variant="primary"
              onClick={() => vaultPutMut.mutate()}
              loading={vaultPutMut.isPending}
              disabled={!vaultForm.namespace.trim() || !vaultForm.label.trim()}
            >
              {t("entities.encryptAndStore", "Zašifrovat a uložit")}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Namespace</Label>
              <Input
                value={vaultForm.namespace}
                onChange={(e) => setVaultForm((form) => ({
                  ...form,
                  namespace: e.target.value,
                }))}
                placeholder="oauth | llm | acm_connection | custom"
              />
            </div>
            <div>
              <Label>Label</Label>
              <Input
                value={vaultForm.label}
                onChange={(e) =>
                  setVaultForm((form) => ({ ...form, label: e.target.value }))}
                placeholder="provider/account"
              />
            </div>
          </div>
          <div>
            <Label>{t("entities.secretPayload", "Tajný JSON payload")}</Label>
            <textarea
              className="mt-1 w-full min-h-32 bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-xs text-white font-mono"
              value={vaultForm.payload}
              onChange={(e) =>
                setVaultForm((form) => ({ ...form, payload: e.target.value }))}
            />
          </div>
          <div>
            <Label>
              {t("entities.publicMetadata", "Veřejná metadata JSON")}
            </Label>
            <textarea
              className="mt-1 w-full min-h-20 bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-xs text-white font-mono"
              value={vaultForm.metadata}
              onChange={(e) =>
                setVaultForm((form) => ({ ...form, metadata: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>{t("entities.safePreview", "Bezpečný preview")}</Label>
              <Input
                value={vaultForm.preview}
                onChange={(e) => setVaultForm((form) => ({
                  ...form,
                  preview: e.target.value,
                }))}
                placeholder="sk-proj…"
              />
            </div>
            <div>
              <Label>Access scope</Label>
              <Select
                className="w-full"
                value={vaultForm.access_scope}
                onChange={(e) =>
                  setVaultForm((form) => ({
                    ...form,
                    access_scope: e.target
                      .value as EntityVaultItem["access_scope"],
                  }))}
              >
                <option value="user_only">user_only</option>
                <option value="user_and_node">user_and_node</option>
                <option value="user_and_node_and_escrow">
                  user_and_node_and_escrow
                </option>
              </Select>
            </div>
          </div>
          <p className="text-[11px] text-gray-600">
            {t(
              "entities.vaultPlaintextHint",
              "Payload se odešle pouze přes same-origin Identity tool, na serveru se envelope-šifruje a do seznamu ani auditu se plaintext nevrací.",
            )}
          </p>
        </div>
      </Modal>

      {/* Show generated key (one-time) */}
      <Modal
        open={!!showKey}
        onClose={() => setShowKey(null)}
        title={t("entities.generatedKeyTitle", "Vygenerovaný API klíč")}
        footer={
          <Button variant="primary" onClick={() => setShowKey(null)}>
            {t("entities.close", "Zavřít")}
          </Button>
        }
      >
        {showKey && (
          <div className="space-y-3">
            <div className="p-3 bg-amber-950/30 border border-amber-500/30 rounded text-xs text-amber-300">
              {t(
                "entities.keyShowOnceWarning",
                "⚠️ Klíč se zobrazí jen tentokrát. Ulož si ho!",
              )}
            </div>
            <div>
              <Label>{showKey.label}</Label>
              <div className="mt-1 flex gap-2">
                <code className="flex-1 bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-xs text-emerald-400 font-mono break-all">
                  {showKey.key}
                </code>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<Check size={12} />}
                  onClick={() => {
                    navigator.clipboard.writeText(showKey.key);
                    toast.success(t("entities.copied", "Zkopírováno"));
                  }}
                >
                  {t("entities.copy", "Kopírovat")}
                </Button>
              </div>
            </div>
            {showKey.mqtt && (
              <div className="p-3 bg-cyan-950/30 border border-cyan-500/30 rounded space-y-2">
                <div className="text-xs font-medium text-cyan-300">
                  {t(
                    "entities.mqttCredentials",
                    "🔌 MQTT credentials (role: {{role}})",
                    {
                      role: showKey.mqtt.role,
                    },
                  )}
                </div>
                <div>
                  <Label>Username</Label>
                  <code className="block mt-1 bg-gray-950 border border-gray-800 rounded px-3 py-2 text-xs text-cyan-400 font-mono">
                    {showKey.mqtt.username}
                  </code>
                </div>
                <div>
                  <Label>Password</Label>
                  <div className="mt-1 flex gap-2">
                    <code className="flex-1 bg-gray-950 border border-gray-800 rounded px-3 py-2 text-xs text-emerald-400 font-mono break-all">
                      {showKey.mqtt.password}
                    </code>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<Check size={12} />}
                      onClick={() => {
                        navigator.clipboard.writeText(showKey.mqtt!.password);
                        toast.success(t("entities.copied", "Zkopírováno"));
                      }}
                    >
                      {t("entities.copy", "Kopírovat")}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Přidat DB přístup — user reprezentující entitu, spojený od vzniku */}
      {dbGrantOpen && detail && (
        <Modal
          open
          onClose={() => setDbGrantOpen(false)}
          title={t("entities.dbAccessForTitle", "DB přístup pro: {{label}}", {
            label: detail.entity.label,
          })}
          footer={dbGrantResult
            ? (
              <Button variant="secondary" onClick={() => setDbGrantOpen(false)}>
                {t("entities.close", "Zavřít")}
              </Button>
            )
            : (
              <>
                <Button
                  variant="secondary"
                  onClick={() => setDbGrantOpen(false)}
                >
                  {t("entities.cancel", "Zrušit")}
                </Button>
                <Button
                  variant="primary"
                  loading={dbGrantMut.isPending}
                  disabled={!dbGrantForm.instance || !dbGrantForm.username}
                  onClick={() => dbGrantMut.mutate()}
                >
                  {t("entities.create", "Vytvořit")}
                </Button>
              </>
            )}
        >
          {dbGrantResult
            ? (
              <div className="space-y-3">
                <div className="text-xs text-amber-400 bg-amber-950/30 border border-amber-900/50 rounded-xl p-2">
                  {t(
                    "entities.connStringWarning",
                    "⚠ Connection string se zobrazuje JEN JEDNOU — ulož si ho teď.",
                  )}
                </div>
                <div className="flex items-center gap-2 p-2 bg-gray-950 border border-gray-800 rounded-xl">
                  <code className="flex-1 text-xs text-emerald-300 font-mono break-all">
                    {dbGrantResult}
                  </code>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(dbGrantResult);
                      toast.success(t("entities.copied", "Zkopírováno"));
                    }}
                  >
                    {t("entities.copy", "Kopírovat")}
                  </Button>
                </div>
              </div>
            )
            : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Instance *</Label>
                    <Select
                      size="sm"
                      className="mt-1"
                      value={dbGrantForm.instance}
                      onChange={(e) =>
                        setDbGrantForm((f) => ({
                          ...f,
                          instance: e.target.value,
                        }))}
                      options={(dbInstancesData?.instances ?? []).map((i) => ({
                        value: i.name,
                        label: `${i.name} (${i.engine})`,
                      }))}
                      placeholder={t(
                        "entities.selectInstancePlaceholder",
                        "— vyber instanci —",
                      )}
                    />
                  </div>
                  <div>
                    <Label>Username</Label>
                    <Input
                      size="sm"
                      className="mt-1 font-mono"
                      value={dbGrantForm.username}
                      onChange={(e) =>
                        setDbGrantForm((f) => ({
                          ...f,
                          username: e.target.value,
                        }))}
                    />
                  </div>
                  <div>
                    <Label>
                      {t("entities.dbNameLabel", "Databáze (prázdné = vše)")}
                    </Label>
                    <Input
                      size="sm"
                      className="mt-1"
                      value={dbGrantForm.db_name}
                      onChange={(e) =>
                        setDbGrantForm((f) => ({
                          ...f,
                          db_name: e.target.value,
                        }))}
                      placeholder="myapp_db"
                    />
                  </div>
                  <div>
                    <Label>{t("entities.privilegesLabel", "Oprávnění")}</Label>
                    <Select
                      size="sm"
                      className="mt-1"
                      value={dbGrantForm.privileges}
                      onChange={(e) =>
                        setDbGrantForm((f) => ({
                          ...f,
                          privileges: e.target.value,
                        }))}
                      options={["ALL", "SELECT", "SELECT,INSERT,UPDATE,DELETE"]}
                    />
                  </div>
                </div>
                <p className="text-[11px] text-gray-500">
                  {t(
                    "entities.dbGrantHint",
                    "Vytvoří DB usera reprezentujícího tuto entitu — link vznikne rovnou, heslo se vygeneruje a uvidíš ho jednou v connection stringu.",
                  )}
                </p>
              </div>
            )}
        </Modal>
      )}

      {/* Confirm clone — ACM-styled (= native confirm() je grafikou cizi) */}
      {confirmCloneFor && (
        <Modal
          open={!!confirmCloneFor}
          onClose={() => setConfirmCloneFor(null)}
          title={t("entities.cloneEntityTitle", "Klonovat entity")}
        >
          <div className="space-y-3">
            <div className="text-sm text-gray-300">
              {t("entities.cloneWord", "Klonovat")}{" "}
              <code className="text-emerald-400">{confirmCloneFor.label}</code>?
            </div>
            <div className="text-xs text-gray-400 bg-gray-950 border border-gray-800 rounded p-3 space-y-1">
              <div>
                {t(
                  "entities.cloneWillCreate",
                  "Vytvoří novou entity se stejnými scopes a:",
                )}
              </div>
              <ul className="ml-4 list-disc space-y-0.5">
                <li>
                  <strong>{confirmCloneFor.api_keys_count}</strong> {t(
                    "entities.cloneApiKeysItem",
                    "API klíčů (= regenerované, nové plaintexty)",
                  )}
                </li>
                <li>
                  <strong>{confirmCloneFor.ssh_keys_count}</strong> {t(
                    "entities.cloneSshKeysItem",
                    "SSH klíčů (= zkopírované, public)",
                  )}
                </li>
                <li>
                  {t(
                    "entities.cloneLlmItem",
                    "LLM external keys + node access + group memberships (= podle původní entity)",
                  )}
                </li>
              </ul>
            </div>
            <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-300">
              <input
                type="checkbox"
                checked={cloneKeysFlag}
                onChange={(e) => setCloneKeysFlag(e.target.checked)}
                className="accent-blue-500"
              />
              <span>
                {t(
                  "entities.cloneRegenerateKeys",
                  "Také regenerovat klíče ({{count}} API + LLM)",
                  {
                    count: confirmCloneFor.api_keys_count,
                  },
                )}
                <span className="text-gray-500 ml-2">
                  {cloneKeysFlag
                    ? t(
                      "entities.cloneKeysHintYes",
                      "← nové plaintexty uvidíš jednou v dalším modalu",
                    )
                    : t(
                      "entities.cloneKeysHintNo",
                      "← jen meta struktura, klíče si přidáš explicitně později",
                    )}
                </span>
              </span>
            </label>
            {cloneKeysFlag && (
              <div className="text-xs text-amber-400 bg-amber-950/30 border border-amber-900/50 rounded p-2">
                {t(
                  "entities.cloneKeysWarningPre",
                  "⚠ Plaintexty nových klíčů uvidíš",
                )}{" "}
                <strong>
                  {t("entities.cloneKeysWarningEmph", "JEN JEDNOU")}
                </strong>{" "}
                {t(
                  "entities.cloneKeysWarningPost",
                  "v modálu po klonování. Použij Export tlačítka pro stažení/kopírování všech najednou.",
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-800">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setConfirmCloneFor(null)}
              >
                {t("entities.cancel", "Zrušit")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                loading={cloneMut.isPending}
                onClick={() => {
                  const id = confirmCloneFor.id;
                  const flag = cloneKeysFlag;
                  setConfirmCloneFor(null);
                  cloneMut.mutate({ id, clone_keys: flag });
                }}
              >
                {t("entities.clone", "Klonovat")}
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {/* Clone result modal — plaintexty kluc zobrazene jen jednou */}
      {cloneResult && (
        <Modal
          open={!!cloneResult}
          onClose={() => setCloneResult(null)}
          title={t("entities.clonedTitle", "Naklonováno: {{label}}", {
            label: cloneResult.label,
          })}
        >
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            <div className="text-xs text-amber-400 bg-amber-950/30 border border-amber-900/50 rounded p-2">
              {t(
                "entities.cloneResultWarning",
                "⚠ Plaintexty se zobrazují JEN JEDNOU. Ulož je teď — po zavření modal je nezískáš zpátky.",
              )}
            </div>

            {/* Export buttons — JSON, .env, copy-all */}
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  const data = {
                    entity_label: cloneResult.label,
                    api_keys: cloneResult.api_keys,
                    llm_keys: cloneResult.llm_keys,
                    exported_at: new Date().toISOString(),
                  };
                  const blob = new Blob([JSON.stringify(data, null, 2)], {
                    type: "application/json",
                  });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `entity-clone-${
                    cloneResult.label.replace(/[^a-z0-9-_]/gi, "_")
                  }-${Date.now()}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success(t("entities.jsonDownloaded", "JSON staženo"));
                }}
              >
                {t("entities.exportJson", "Export JSON")}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  const lines: string[] = [
                    `# Entity clone: ${cloneResult.label}`,
                    `# Exported: ${new Date().toISOString()}`,
                    "",
                  ];
                  if (cloneResult.api_keys.length > 0) {
                    lines.push("# API keys (adx_*)");
                    (Array.isArray(cloneResult.api_keys)
                      ? cloneResult.api_keys
                      : []).forEach((k) => {
                        const envKey = k.label.toUpperCase().replace(
                          /[^A-Z0-9_]/g,
                          "_",
                        );
                        lines.push(`ACM_API_KEY_${envKey}=${k.plaintext}`);
                      });
                    lines.push("");
                  }
                  if (cloneResult.llm_keys.length > 0) {
                    lines.push("# LLM external keys (sk-acm-*)");
                    (Array.isArray(cloneResult.llm_keys)
                      ? cloneResult.llm_keys
                      : []).forEach((k) => {
                        const envKey = k.label.toUpperCase().replace(
                          /[^A-Z0-9_]/g,
                          "_",
                        );
                        lines.push(`LLM_KEY_${envKey}=${k.plaintext}`);
                      });
                  }
                  const blob = new Blob([lines.join("\n")], {
                    type: "text/plain",
                  });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `entity-clone-${
                    cloneResult.label.replace(/[^a-z0-9-_]/gi, "_")
                  }-${Date.now()}.env`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success(t("entities.envDownloaded", ".env staženo"));
                }}
              >
                {t("entities.exportEnv", "Export .env")}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  const all = [
                    ...cloneResult.api_keys.map((k) =>
                      `${k.label}: ${k.plaintext}`
                    ),
                    ...cloneResult.llm_keys.map((k) =>
                      `${k.label}: ${k.plaintext}`
                    ),
                  ].join("\n");
                  navigator.clipboard.writeText(all);
                  toast.success(
                    t(
                      "entities.keysCopiedCount",
                      "{{count}} klíčů zkopírováno",
                      {
                        count: cloneResult.api_keys.length +
                          cloneResult.llm_keys.length,
                      },
                    ),
                  );
                }}
              >
                {t("entities.copyAll", "Kopírovat vše")}
              </Button>
            </div>
            {cloneResult.api_keys.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-gray-300 mb-2">
                  {t("entities.apiKeysHeader", "API klíče")}{" "}
                  ({cloneResult.api_keys.length})
                </div>
                <div className="space-y-1.5">
                  {cloneResult.api_keys.map((k) => (
                    <div
                      key={k.label}
                      className="bg-gray-950 border border-gray-800 rounded p-2"
                    >
                      <div className="text-[10px] text-gray-500 mb-0.5">
                        {k.label}
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="text-xs font-mono text-emerald-400 flex-1 break-all">
                          {k.plaintext}
                        </code>
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(k.plaintext);
                            toast.success(t("entities.copied", "Zkopírováno"));
                          }}
                          className="text-gray-500 hover:text-white"
                        >
                          <Copy size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {cloneResult.llm_keys.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-gray-300 mb-2">
                  {t("entities.llmKeysHeader", "LLM klíče")}{" "}
                  ({cloneResult.llm_keys.length})
                </div>
                <div className="space-y-1.5">
                  {cloneResult.llm_keys.map((k) => (
                    <div
                      key={k.label}
                      className="bg-gray-950 border border-gray-800 rounded p-2"
                    >
                      <div className="text-[10px] text-gray-500 mb-0.5">
                        {k.label}
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="text-xs font-mono text-blue-400 flex-1 break-all">
                          {k.plaintext}
                        </code>
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(k.plaintext);
                            toast.success(t("entities.copied", "Zkopírováno"));
                          }}
                          className="text-gray-500 hover:text-white"
                        >
                          <Copy size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {cloneResult.api_keys.length === 0 &&
              cloneResult.llm_keys.length === 0 && (
              <div className="text-xs text-gray-500 italic">
                {t(
                  "entities.cloneNoKeysMessage",
                  "Original entity neměla žádné API/LLM klíče k regeneraci. Naklonována jen meta + SSH/node_access (= viz stats).",
                )}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── GroupsView — správa skupin (sdílené role/scopes pro entity) ──────────────

interface GroupRow {
  id: string;
  name: string;
  description: string | null;
  metadata: Record<string, unknown>;
  protected: boolean;
  member_count: number;
  tenant_id: string | null;
  tenant_slug: string | null;
  tenant_name: string | null;
  created_at: string;
}

interface GroupDetail {
  group: GroupRow;
  members: Array<
    {
      entity_id: string;
      label: string;
      type: string;
      keycloak_sub: string | null;
      added_at: string;
    }
  >;
}

// ── Sessions View — přehled aktivních sessions napříč entitami ───────────────

function SessionsView() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["all-sessions"],
    queryFn: () =>
      mcpCall<{ ok: boolean; sessions: Array<Record<string, unknown>> }>(
        "toolbox.session_manage",
        { action: "list" },
      ),
    refetchInterval: 15_000,
  });

  const allSessions = (data?.sessions ?? []) as Array<Record<string, unknown>>;

  // Filter by search
  const q = search.toLowerCase();
  const sessions = q
    ? allSessions.filter((s) =>
      ((s.entity_label as string) ?? "").toLowerCase().includes(q) ||
      ((s.domain as string) ?? "").toLowerCase().includes(q) ||
      ((s.ip_address as string) ?? "").toLowerCase().includes(q) ||
      ((s.node_id as string) ?? "").toLowerCase().includes(q)
    )
    : allSessions;

  // Group by node → entity_id → domain
  type SRow = Record<string, unknown>;
  const byNode: Record<
    string,
    Record<string, { label: string; sessions: SRow[] }>
  > = {};
  for (const s of sessions) {
    const node = (s.node_id as string) || "unknown";
    const eid = (s.entity_id as string) || "?";
    if (!byNode[node]) byNode[node] = {};
    if (!byNode[node][eid]) {
      byNode[node][eid] = {
        label: (s.entity_label as string) || eid,
        sessions: [],
      };
    }
    byNode[node][eid].sessions.push(s);
  }

  const revokeMut = useMutation({
    mutationFn: (sessionId: string) =>
      mcpCall("toolbox.session_manage", {
        action: "revoke",
        session_id: sessionId,
      }),
    onSuccess: () => {
      toast.success(t("entities.sessionLoggedOut", "Session odhlášena"));
      refetch();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  // Revoke all sessions for entity on one specific node
  const revokeNodeMut = useMutation({
    mutationFn: (ids: string[]) =>
      Promise.all(
        ids.map((id) =>
          mcpCall("toolbox.session_manage", {
            action: "revoke",
            session_id: id,
          })
        ),
      ),
    onSuccess: () => {
      toast.success(t("entities.sessionsLoggedOut", "Sessions odhlášeny"));
      refetch();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  if (isLoading) {
    return (
      <div className="py-8 text-center text-gray-500 text-sm">
        {t("entities.loading", "Načítám…")}
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <EmptyState
        icon={<Globe size={24} />}
        title={t("entities.noActiveSessionsTitle", "Žádné aktivní sessions")}
        description={t(
          "entities.noActiveSessionsDescription",
          "Nikdo není přihlášený (nebo sessions ještě nebyly zaznamenány).",
        )}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex-1 relative">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t(
              "entities.sessionsSearchPlaceholder",
              "Hledat uživatele, doménu, IP, nod…",
            )}
            className="w-full bg-gray-900 border border-gray-800 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-blue-500"
          />
        </div>
        <span className="text-sm text-gray-500 shrink-0">
          {sessions.length}
          {q
            ? ` ${
              t("entities.ofTotal", "z {{total}}", {
                total: allSessions.length,
              })
            }`
            : ""}
        </span>
        <RefreshButton onRefresh={refetch} loading={isLoading} />
      </div>

      {Object.entries(byNode).map(([node, byEntity]) => {
        const nodeTotal = Object.values(byEntity).reduce(
          (n, u) => n + u.sessions.length,
          0,
        );
        return (
          <Card key={node}>
            <CardHeader>
              <CardTitle icon={Server}>
                {node}{" "}
                <span className="text-gray-500 font-normal">({nodeTotal})</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {Object.entries(byEntity).map((
                [eid, { label, sessions: userSessions }],
              ) => (
                <UserSessionRow
                  key={eid}
                  entityId={eid}
                  label={label}
                  sessions={userSessions}
                  expandedByDefault={!!q}
                  onRevoke={(id) => revokeMut.mutate(id)}
                  onRevokeNode={async () =>
                    await themedConfirm(
                      t(
                        "entities.confirmLogoutAllModules",
                        "Odhlásit {{label}} ze všech modulů na {{node}}?",
                        { label, node },
                      ),
                    ) &&
                    revokeNodeMut.mutate(
                      userSessions.map((s) => s.id as string),
                    )}
                />
              ))}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function UserSessionRow({
  entityId: _eid,
  label,
  sessions,
  expandedByDefault,
  onRevoke,
  onRevokeNode,
}: {
  entityId: string;
  label: string;
  sessions: Record<string, unknown>[];
  expandedByDefault: boolean;
  onRevoke: (id: string) => void;
  onRevokeNode: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(expandedByDefault);
  const domains = [
    ...new Set(sessions.map((s) => (s.domain as string) || "—")),
  ];
  const firstIp = sessions.find((s) => s.ip_address)?.ip_address as
    | string
    | undefined;
  const authType = sessions[0]?.auth_type as string | undefined;
  const lastActive = sessions.reduce<string | undefined>((best, s) => {
    const ts = s.last_active as string || s.created_at as string;
    return !best || ts > best ? ts : best;
  }, undefined);
  const ageMs = lastActive ? Date.now() - new Date(lastActive).getTime() : null;
  const ageStr = ageMs == null
    ? "—"
    : ageMs < 60_000
    ? `${Math.round(ageMs / 1000)}s`
    : ageMs < 3_600_000
    ? `${Math.round(ageMs / 60_000)}m`
    : `${Math.round(ageMs / 3_600_000)}h`;

  return (
    <div className="border-b border-gray-800/40 last:border-0">
      {/* User header row */}
      <div
        className="flex items-center gap-2 px-4 py-2.5 cursor-pointer hover:bg-gray-800/20 select-none"
        onClick={() => setOpen((o) => !o)}
      >
        {open
          ? <ChevronDown size={13} className="text-gray-600 shrink-0" />
          : <ChevronRight size={13} className="text-gray-600 shrink-0" />}
        <div
          className={`w-2 h-2 rounded-full shrink-0 ${
            authType === "keycloak" ? "bg-blue-500" : "bg-emerald-500"
          }`}
        />
        <span className="text-sm font-medium text-white">{label}</span>
        <span
          className={`text-[9px] px-1.5 py-0.5 rounded shrink-0 ${
            authType === "keycloak"
              ? "bg-blue-500/15 text-blue-400"
              : "bg-gray-700 text-gray-400"
          }`}
        >
          {authType === "keycloak" ? "SSO" : t("entities.password", "heslo")}
        </span>
        {!open && (
          <span className="text-[10px] text-gray-600 truncate">
            {domains.join(", ")}
          </span>
        )}
        <span className="text-[10px] text-gray-700 ml-auto shrink-0">
          {firstIp && <span className="mr-1">{firstIp}</span>}
          {ageStr}
        </span>
        <button
          onClick={async (e) => {
            e.stopPropagation();
            onRevokeNode();
          }}
          className="text-gray-700 hover:text-red-400 p-1 shrink-0 ml-1"
          title={t(
            "entities.logoutFromNode",
            "Odhlásit {{label}} z tohoto nodu",
            { label },
          )}
        >
          <LogOut size={12} />
        </button>
      </div>

      {/* Expanded: modules list */}
      {open && (
        <div className="ml-8 border-l border-gray-800/40 divide-y divide-gray-800/20">
          {sessions.map((s) => (
            <div
              key={s.id as string}
              className="flex items-center gap-2 px-3 py-2"
            >
              <Layers size={11} className="text-gray-700 shrink-0" />
              <span className="text-[11px] font-mono text-gray-400">
                {(s.domain as string) || "—"}
              </span>
              <span className="text-[10px] text-gray-600 ml-auto">
                {s.ip_address && <span>{s.ip_address as string} ·</span>}
                {new Date((s.last_active as string) || (s.created_at as string))
                  .toLocaleString(
                    "cs",
                  )}
              </span>
              <button
                onClick={async () =>
                  await themedConfirm(
                    t("entities.confirmLogout", "Odhlásit tuto session?"),
                  ) &&
                  onRevoke(s.id as string)}
                className="text-gray-700 hover:text-red-400 p-1 shrink-0"
                title={t("entities.logoutFromModule", "Odhlásit z modulu")}
              >
                <LogOut size={11} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GroupsView() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);

  // Form
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [acmRole, setAcmRole] = useState("");

  const { data: groupsData, refetch } = useQuery<
    { ok: boolean; groups: GroupRow[] }
  >({
    queryKey: ["groups"],
    queryFn: () =>
      mcpCall<{ ok: boolean; groups: GroupRow[] }>("identity.group_list"),
  });
  const { data: groupRoles } = useQuery<{
    roles?: Array<{ id: string; label: string }>;
  }>({
    queryKey: ["identity-roles"],
    queryFn: () => mcpCall("identity.role_list"),
    staleTime: 5 * 60_000,
  });

  const { data: detailData, refetch: refetchDetail } = useQuery<
    GroupDetail & { ok: boolean }
  >({
    queryKey: ["group-detail", selectedId],
    queryFn: () =>
      mcpCall<GroupDetail & { ok: boolean }>("identity.group_get", {
        id: selectedId!,
      }),
    enabled: !!selectedId,
  });

  const createMut = useMutation({
    mutationFn: () =>
      mcpCall("identity.group_create", {
        name,
        description: description || undefined,
        acm_role: acmRole || undefined,
      }),
    onSuccess: () => {
      toast.success(t("entities.groupCreated", "Skupina vytvořena"));
      setCreateOpen(false);
      setName("");
      setDescription("");
      setAcmRole("");
      qc.invalidateQueries({ queryKey: ["groups"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const delGroupMut = useMutation({
    mutationFn: (id: string) => mcpCall("identity.group_delete", { id }),
    onSuccess: () => {
      toast.success(t("entities.groupDeleted", "Smazáno"));
      setSelectedId(null);
      qc.invalidateQueries({ queryKey: ["groups"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const removeMemberMut = useMutation({
    mutationFn: (entityId: string) =>
      mcpCall("identity.group_member_remove", {
        id: selectedId,
        entity_id: entityId,
      }),
    onSuccess: () => {
      toast.success(t("entities.memberRemoved", "Člen odebrán"));
      refetchDetail();
      refetch();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const groups = groupsData?.groups ?? [];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-xs text-gray-500">
          {t(
            "entities.groupsDescription",
            "Skupiny entity sdílejí ACM role/scopes. Entity v skupině zdědí scopes (admin/operator/viewer).",
          )}
        </p>
        <Button
          variant="primary"
          size="sm"
          icon={<Plus size={12} />}
          onClick={() => setCreateOpen(true)}
        >
          {t("entities.newGroup", "Nová skupina")}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>
              {t("entities.groupsHeader", "Skupiny")} ({groups.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {groups.length === 0
              ? (
                <EmptyState
                  title={t("entities.noGroupsTitle", "Žádné skupiny")}
                  description={t(
                    "entities.noGroupsDescription",
                    "Vytvoř první skupinu.",
                  )}
                />
              )
              : (
                <div className="space-y-1">
                  {groups.map((g) => (
                    <button
                      key={g.id}
                      onClick={() => setSelectedId(g.id)}
                      className={`w-full text-left p-3 rounded border text-xs transition-colors ${
                        selectedId === g.id
                          ? "bg-blue-950/30 border-blue-500/40"
                          : "bg-gray-900/40 border-gray-800 hover:border-gray-700"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-white font-medium">
                            {g.name}
                          </span>
                          {g.protected && (
                            <span className="ml-2 text-[10px] text-amber-400">
                              protected
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-gray-400">
                          {(g.metadata?.acm_role as string | undefined) && (
                            <span className="inline-block px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 text-[10px] font-mono">
                              {g.metadata.acm_role as string}
                            </span>
                          )}
                          <span>
                            <Users size={11} className="inline" />{" "}
                            {g.member_count}
                          </span>
                        </div>
                      </div>
                      {g.description && (
                        <p className="text-gray-500 mt-1">{g.description}</p>
                      )}
                    </button>
                  ))}
                </div>
              )}
          </CardContent>
        </Card>

        {selectedId && detailData && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{detailData.group.name}</span>
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    icon={<Plus size={12} />}
                    onClick={() => setAddMemberOpen(true)}
                  >
                    {t("entities.addMembers", "Přidat členy")}
                  </Button>
                  {!detailData.group.protected && (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<Trash2 size={12} />}
                      onClick={async () => {
                        if (
                          await themedConfirm(
                            t(
                              "entities.confirmDeleteGroup",
                              'Smazat skupinu "{{name}}"? Členové zůstanou (entity nedotčené).',
                              { name: detailData.group.name },
                            ),
                          )
                        ) {
                          delGroupMut.mutate(detailData.group.id);
                        }
                      }}
                    >
                      {t("entities.delete", "Smazat")}
                    </Button>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {detailData.group.description && (
                <p className="text-xs text-gray-400">
                  {detailData.group.description}
                </p>
              )}
              <div className="text-xs text-gray-500">
                <span className="text-gray-400">ACM role:</span>{" "}
                <code className="text-red-400">
                  {(detailData.group.metadata?.acm_role as
                    | string
                    | undefined) ??
                    t("entities.optionNone", "— žádná")}
                </code>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-400 mb-2">
                  {t("entities.membersHeader", "Členové")}{" "}
                  ({detailData.members.length})
                </div>
                {detailData.members.length === 0
                  ? (
                    <p className="text-xs text-gray-500">
                      {t("entities.noMembers", "Žádní členové.")}
                    </p>
                  )
                  : (
                    <div className="space-y-1">
                      {detailData.members.map((m) => (
                        <div
                          key={m.entity_id}
                          className="flex items-center justify-between p-2 bg-gray-900/40 rounded text-xs"
                        >
                          <div>
                            <span className="text-gray-300">{m.label}</span>
                            <span className="ml-2 text-[10px] text-gray-500">
                              {m.type}
                            </span>
                            {m.keycloak_sub && (
                              <span className="ml-2 text-[10px] text-blue-400">
                                SSO
                              </span>
                            )}
                          </div>
                          <button
                            onClick={async () => {
                              if (
                                await themedConfirm(
                                  t(
                                    "entities.confirmRemoveMember",
                                    "Odebrat {{label}} ze skupiny?",
                                    { label: m.label },
                                  ),
                                )
                              ) {
                                removeMemberMut.mutate(m.entity_id);
                              }
                            }}
                            className="text-gray-600 hover:text-red-400 p-1"
                            title={t("entities.remove", "Odebrat")}
                          >
                            <Trash2 size={11} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {createOpen && (
        <Modal
          open
          onClose={() => setCreateOpen(false)}
          title={t("entities.newGroup", "Nová skupina")}
          footer={
            <>
              <Button variant="secondary" onClick={() => setCreateOpen(false)}>
                {t("entities.cancel", "Zrušit")}
              </Button>
              <Button
                variant="primary"
                loading={createMut.isPending}
                disabled={!name}
                onClick={() => createMut.mutate()}
              >
                {t("entities.create", "Vytvořit")}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <div>
              <Label>{t("entities.nameLabel", "Název")} *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t(
                  "entities.groupNamePlaceholder",
                  "např. mqtt-publishers",
                )}
              />
            </div>
            <div>
              <Label>{t("entities.descriptionLabel", "Popis")}</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div>
              <Label>
                {t("entities.acmRoleOptionalLabel", "ACM role (volitelná)")}
              </Label>
              <Select
                size="sm"
                value={acmRole}
                onChange={(e) => setAcmRole(e.target.value)}
                className="w-full"
                options={[
                  {
                    value: "",
                    label: t(
                      "entities.noRoleGroupOption",
                      "— žádná (jen členství, scopes přes přímo na entity)",
                    ),
                  },
                  ...(groupRoles?.roles ?? []).map((r) => ({
                    value: r.id,
                    label: r.label,
                  })),
                ]}
              />
            </div>
          </div>
        </Modal>
      )}

      {addMemberOpen && selectedId && (
        <AddMembersModal
          groupId={selectedId}
          existingMemberIds={detailData?.members.map((m) => m.entity_id) ?? []}
          onClose={() => setAddMemberOpen(false)}
          onSaved={() => {
            refetchDetail();
            refetch();
          }}
        />
      )}
    </div>
  );
}

function AddMembersModal({
  groupId,
  existingMemberIds,
  onClose,
  onSaved,
}: {
  groupId: string;
  existingMemberIds: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data } = useQuery<{ ok: boolean; entities: Entity[] }>({
    queryKey: ["entities-for-group"],
    queryFn: () =>
      mcpCall<{ ok: boolean; entities: Entity[] }>("identity.entity_list"),
  });

  const mut = useMutation({
    mutationFn: () =>
      mcpCall("identity.group_members_add", {
        id: groupId,
        entity_ids: [...selected],
      }),
    onSuccess: () => {
      toast.success(t("entities.membersAdded", "Členové přidáni"));
      onSaved();
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const candidates = (data?.entities ?? [])
    .filter((e) => !existingMemberIds.includes(e.id))
    .filter((e) =>
      !search || e.label.toLowerCase().includes(search.toLowerCase())
    )
    .slice(0, 100);

  return (
    <Modal
      open
      onClose={onClose}
      title={t("entities.addMembersTitle", "Přidat členy do skupiny")}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("entities.cancel", "Zrušit")}
          </Button>
          <Button
            variant="primary"
            loading={mut.isPending}
            disabled={selected.size === 0}
            onClick={() => mut.mutate()}
          >
            {t("entities.addCount", "Přidat ({{count}})", {
              count: selected.size,
            })}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("entities.searchEntityPlaceholder", "Hledat entitu…")}
          className="w-full bg-gray-950 border border-gray-800 rounded px-3 py-2 text-sm text-white"
        />
        <div className="space-y-1 max-h-80 overflow-y-auto">
          {candidates.length === 0
            ? (
              <p className="text-xs text-gray-500 p-2">
                {t("entities.noAvailableEntities", "Žádné dostupné entity.")}
              </p>
            )
            : candidates.map((e) => {
              const Icon = TYPE_META[e.type as EntityType]?.icon ?? Shield;
              return (
                <label
                  key={e.id}
                  className="flex items-center gap-2 p-2 bg-gray-900/40 rounded cursor-pointer hover:bg-gray-900/60"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(e.id)}
                    onChange={(ev) => {
                      setSelected((s) => {
                        const n = new Set(s);
                        if (ev.target.checked) n.add(e.id);
                        else n.delete(e.id);
                        return n;
                      });
                    }}
                  />
                  <Icon
                    size={12}
                    className={TYPE_META[e.type as EntityType]?.color ??
                      "text-gray-400"}
                  />
                  <span className="text-xs text-gray-300">{e.label}</span>
                  <span className="text-[10px] text-gray-500">{e.type}</span>
                </label>
              );
            })}
        </div>
      </div>
    </Modal>
  );
}

// ── KcUserCard — info + actions pro KC usera linknutého na entity ─────────────

interface KcUserData {
  id: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  enabled?: boolean;
  emailVerified?: boolean;
}

function KcUserCard(
  { sub, entityId: _entityId, onChanged }: {
    sub: string;
    entityId: string;
    onChanged: () => void;
  },
) {
  const { t } = useTranslation();
  const [pwOpen, setPwOpen] = useState(false);
  const [editKcOpen, setEditKcOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [tempPassword, setTempPassword] = useState(true);
  const [editForm, setEditForm] = useState<
    { email: string; firstName: string; lastName: string; enabled: boolean }
  >({
    email: "",
    firstName: "",
    lastName: "",
    enabled: true,
  });

  const { data, refetch } = useQuery<
    {
      ok: boolean;
      realm: string;
      user: KcUserData;
      roles: Array<{ id: string; name: string }>;
    }
  >({
    queryKey: ["kc-user", sub],
    queryFn: () =>
      mcpCall<
        {
          ok: boolean;
          realm: string;
          user: KcUserData;
          roles: Array<{ id: string; name: string }>;
        }
      >("sso.keycloak_user_get", { sub }),
  });

  const resetMut = useMutation({
    mutationFn: () =>
      mcpCall("sso.keycloak_user_password_reset", {
        sub,
        password: newPassword,
        temporary: tempPassword,
      }),
    onSuccess: () => {
      toast.success(t("entities.passwordSet", "Heslo nastaveno"));
      setPwOpen(false);
      setNewPassword("");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateMut = useMutation({
    mutationFn: () => mcpCall("sso.keycloak_user_update", { sub, ...editForm }),
    onSuccess: () => {
      toast.success(
        t("entities.kcUserUpdated", "Keycloak uživatel aktualizován"),
      );
      setEditKcOpen(false);
      refetch();
      onChanged();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleMut = useMutation({
    mutationFn: () =>
      mcpCall("sso.keycloak_user_update", {
        sub,
        enabled: !data?.user.enabled,
      }),
    onSuccess: () => {
      toast.success(t("entities.kcUserToggled", "Keycloak uživatel přepnut"));
      refetch();
      onChanged();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!data) return null;
  const u = data.user;

  return (
    <div className="p-3 bg-blue-950/20 border border-blue-500/20 rounded-xl space-y-2 text-xs">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="info" className="text-[10px]">Keycloak</Badge>
          <span className="text-white font-medium">{u.username}</span>
          {u.enabled === false && (
            <span className="text-amber-400 text-[10px]">
              {t("entities.disabledInKc", "disabled v KC")}
            </span>
          )}
        </div>
        <div className="flex gap-1">
          <button
            onClick={async () => {
              setEditForm({
                email: u.email ?? "",
                firstName: u.firstName ?? "",
                lastName: u.lastName ?? "",
                enabled: u.enabled ?? true,
              });
              setEditKcOpen(true);
            }}
            className="text-gray-400 hover:text-white p-1"
            title={t("entities.editInKc", "Upravit v KC")}
          >
            <Edit size={11} />
          </button>
          <button
            onClick={() => setPwOpen(true)}
            className="text-gray-400 hover:text-white p-1"
            title={t("entities.resetPasswordTitle", "Reset hesla")}
          >
            <Key size={11} />
          </button>
          <button
            onClick={async () => {
              if (
                await themedConfirm(
                  u.enabled
                    ? t(
                      "entities.confirmDisableKcUser",
                      "Disabluj KC usera {{username}}?",
                      {
                        username: u.username,
                      },
                    )
                    : t(
                      "entities.confirmEnableKcUser",
                      "Enable KC usera {{username}}?",
                      {
                        username: u.username,
                      },
                    ),
                )
              ) toggleMut.mutate();
            }}
            className="text-gray-400 hover:text-amber-400 p-1"
            title={u.enabled
              ? t("entities.disableInKc", "Disable v KC")
              : t("entities.enableInKc", "Enable v KC")}
          >
            {u.enabled ? <Lock size={11} /> : <LockOpen size={11} />}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
        <div>
          <span className="text-gray-500">Email:</span>{" "}
          <span className="text-gray-300">{u.email ?? "—"}</span>
        </div>
        <div>
          <span className="text-gray-500">Verified:</span>{" "}
          <span className="text-gray-300">
            {u.emailVerified
              ? t("entities.yes", "ano")
              : t("entities.no", "ne")}
          </span>
        </div>
        <div>
          <span className="text-gray-500">
            {t("entities.nameFieldLabel", "Jméno")}:
          </span>{" "}
          <span className="text-gray-300">
            {u.firstName ?? ""} {u.lastName ?? ""}
          </span>
        </div>
        <div>
          <span className="text-gray-500">Sub:</span>{" "}
          <code className="text-gray-300 font-mono">{u.id.slice(0, 8)}…</code>
        </div>
      </div>
      {data.roles.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {data.roles.map((r) => (
            <code
              key={r.id}
              className="inline-block px-1.5 py-0.5 rounded bg-gray-800 text-[10px] text-blue-300 font-mono"
            >
              {r.name}
            </code>
          ))}
        </div>
      )}

      {/* Reset password modal */}
      {pwOpen && (
        <Modal
          open
          onClose={() => setPwOpen(false)}
          title={t(
            "entities.resetPasswordModalTitle",
            "Reset hesla: {{username}}",
            {
              username: u.username,
            },
          )}
          footer={
            <>
              <Button variant="secondary" onClick={() => setPwOpen(false)}>
                {t("entities.cancel", "Zrušit")}
              </Button>
              <Button
                variant="primary"
                loading={resetMut.isPending}
                disabled={!newPassword}
                onClick={() => resetMut.mutate()}
              >
                {t("entities.setPassword", "Nastavit heslo")}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <div>
              <Label>{t("entities.newPasswordLabel", "Nové heslo")}</Label>
              <input
                type="password"
                className="mt-1 w-full bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-white"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <label className="flex items-center gap-2 text-xs text-gray-300">
              <input
                type="checkbox"
                checked={tempPassword}
                onChange={(e) => setTempPassword(e.target.checked)}
              />
              {t(
                "entities.temporaryPasswordLabel",
                "Dočasné — user musí změnit při dalším loginu",
              )}
            </label>
          </div>
        </Modal>
      )}

      {/* Edit KC user modal */}
      {editKcOpen && (
        <Modal
          open
          onClose={() => setEditKcOpen(false)}
          title={t(
            "entities.editKcUserTitle",
            "Upravit KC user: {{username}}",
            {
              username: u.username,
            },
          )}
          footer={
            <>
              <Button variant="secondary" onClick={() => setEditKcOpen(false)}>
                {t("entities.cancel", "Zrušit")}
              </Button>
              <Button
                variant="primary"
                loading={updateMut.isPending}
                onClick={() => updateMut.mutate()}
              >
                {t("entities.save", "Uložit")}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <div>
              <Label>Email</Label>
              <input
                className="mt-1 w-full bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-white"
                value={editForm.email}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>First name</Label>
                <input
                  className="mt-1 w-full bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-white"
                  value={editForm.firstName}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, firstName: e.target.value }))}
                />
              </div>
              <div>
                <Label>Last name</Label>
                <input
                  className="mt-1 w-full bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-white"
                  value={editForm.lastName}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, lastName: e.target.value }))}
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={editForm.enabled}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, enabled: e.target.checked }))}
              />
              {t("entities.enabledInKeycloak", "Enabled v Keycloaku")}
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}
